import React, { useState, useEffect, useRef, useCallback } from "react";
import { authStore } from "../lib/api";
import { GoogleMap, Polygon, Polyline, Marker } from "@react-google-maps/api";
import {
  Loader2, X, Minus, Square, Hexagon, Type, MousePointer, Trash2, Save, Camera, Box, AlertTriangle,
  Map as MapIcon, Layers, Crop, Ruler, Pencil, PenLine, Eraser, Mountain,
  Undo2, Redo2, FileText, MessageSquare, Maximize2, Minimize2, MoveDiagonal, Sparkles, Images, ZoomIn,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import ProposalShareButton from "./ProposalShareButton";
import { toast } from "sonner";
import { GoogleMapError, useGMaps, asLL } from "../lib/gmaps";

const COLORS = ["#ef4444", "#6366f1", "#16a34a", "#f59e0b", "#0ea5e9", "#ffffff", "#111827"];
const LABEL_COLORS = ["#111827", "#ef4444", "#2563eb", "#16a34a", "#f59e0b", "#ffffff"];
const LABEL_SIZES = [12, 16, 22, 30];
const BASE_W = 960;
const BASE_H = 540;
const SNAP = 12;
const ORTHO = 12;
const HANDLE = 12;
const M2FT = 3.28084;
const MIN_BLD_CLEAR_FT = 4;
const UTIL_ICON_KEYS = ["water", "gas", "electric", "pole", "septic", "storm"];
const LINK_ICON_KEYS = [...UTIL_ICON_KEYS, "entrance"];
// 每英尺经验单价区间(USD): 开挖布管/接线; entrance=步道铺装(按约4ft宽)
const UTIL_COST_FT = { water: [50, 90], gas: [60, 120], electric: [55, 110], pole: [55, 110], septic: [80, 150], storm: [40, 80], entrance: [50, 100] };
const round50 = (v) => Math.max(50, Math.round(v / 50) * 50);
const CARDINALS_ZH = ["北", "东北", "东", "东南", "南", "西南", "西", "西北"];
const CARDINALS_EN = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const cardinalOf = (deg, zh) => (zh ? CARDINALS_ZH : CARDINALS_EN)[Math.round((((deg % 360) + 360) % 360) / 45) % 8];

// 地界线内缩 ft 英尺（加州 ADU 侧/后 4ft 退界参考区）；返回 [[lat,lng],...] 或 null
function insetPolygon(coords, ft) {
  if (!coords || coords.length < 3) return null;
  let pts = coords.slice();
  const f0 = pts[0], ln = pts[pts.length - 1];
  if (f0[0] === ln[0] && f0[1] === ln[1]) pts = pts.slice(0, -1);
  if (pts.length < 3) return null;
  const latMid = pts.reduce((s, c) => s + c[0], 0) / pts.length;
  const mLat = 364567;
  const mLng = 365221 * Math.cos((latMid * Math.PI) / 180);
  const P = pts.map(([la, lg]) => ({ x: lg * mLng, y: la * mLat }));
  let area = 0;
  for (let i = 0; i < P.length; i++) { const j = (i + 1) % P.length; area += P[i].x * P[j].y - P[j].x * P[i].y; }
  if (!area) return null;
  const ccw = area > 0;
  const lines = P.map((p1, i) => {
    const p2 = P[(i + 1) % P.length];
    const dx = p2.x - p1.x, dy = p2.y - p1.y, len = Math.hypot(dx, dy) || 1;
    const nx = (ccw ? -dy : dy) / len, ny = (ccw ? dx : -dx) / len;
    return { px: p1.x + nx * ft, py: p1.y + ny * ft, dx, dy };
  });
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const a = lines[(i - 1 + lines.length) % lines.length], b = lines[i];
    const det = a.dx * b.dy - a.dy * b.dx;
    if (Math.abs(det) < 1e-9) { out.push([b.py / mLat, b.px / mLng]); continue; }
    const t = ((b.px - a.px) * b.dy - (b.py - a.py) * b.dx) / det;
    out.push([(a.py + a.dy * t) / mLat, (a.px + a.dx * t) / mLng]);
  }
  let a2 = 0;
  const Q = out.map(([la, lg]) => ({ x: lg * mLng, y: la * mLat }));
  for (let i = 0; i < Q.length; i++) { const j = (i + 1) % Q.length; a2 += Q[i].x * Q[j].y - Q[j].x * Q[i].y; }
  if (!isFinite(a2) || Math.abs(a2) < 50 || Math.abs(a2) >= Math.abs(area)) return null;
  return out;
}

// 两多边形是否重叠(角点互含或边相交), 用于 ADU 模板避让主屋提示
function polysOverlap(a, b) {
  if (!a || !b || a.length < 3 || b.length < 3) return false;
  const inside = (pt, poly) => {
    let ok = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const pi = poly[i], pj = poly[j];
      if ((pi.y > pt.y) !== (pj.y > pt.y) && pt.x < ((pj.x - pi.x) * (pt.y - pi.y)) / (pj.y - pi.y) + pi.x) ok = !ok;
    }
    return ok;
  };
  if (a.some((p) => inside(p, b)) || b.some((p) => inside(p, a))) return true;
  const segX = (p1, p2, p3, p4) => {
    const d = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x);
    if (Math.abs(d) < 1e-9) return false;
    const t = ((p3.x - p1.x) * (p4.y - p3.y) - (p3.y - p1.y) * (p4.x - p3.x)) / d;
    const u = ((p3.x - p1.x) * (p2.y - p1.y) - (p3.y - p1.y) * (p2.x - p1.x)) / d;
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  };
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (segX(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length])) return true;
    }
  }
  return false;
}

// ADU 形状角点(rect/polygon)
function aduCorners(s) {
  if (s.labelTag !== "ADU") return null;
  if (s.type === "rect") {
    const r = rectNorm(s.points);
    return [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }];
  }
  if (s.type === "polygon" && s.points.length >= 3) return s.points;
  return null;
}

// 两多边形最小间距(px, 不重叠时)
function polyMinDist(a, b) {
  let best = Infinity;
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      best = Math.min(best,
        distToSeg(a[i], b[j], b[(j + 1) % b.length]),
        distToSeg(b[j], a[i], a[(i + 1) % a.length]));
    }
  }
  return best;
}

// 多边形面积(平方英尺)
function polyAreaSqft(coords) {
  if (!coords || coords.length < 3) return 0;
  let pts = coords.slice();
  const f0 = pts[0], ln = pts[pts.length - 1];
  if (f0[0] === ln[0] && f0[1] === ln[1]) pts = pts.slice(0, -1);
  if (pts.length < 3) return 0;
  const latMid = pts.reduce((s, c) => s + c[0], 0) / pts.length;
  const mLat = 364567;
  const mLng = 365221 * Math.cos((latMid * Math.PI) / 180);
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    a += pts[i][1] * mLng * (pts[j][0] * mLat) - pts[j][1] * mLng * (pts[i][0] * mLat);
  }
  return Math.abs(a / 2);
}

const TOOLS = [
  { k: "select", zh: "选择/移动", en: "Select", Icon: MousePointer },
  { k: "line", zh: "直线", en: "Line", Icon: Minus },
  { k: "rect", zh: "矩形", en: "Rect", Icon: Square },
  { k: "polygon", zh: "多边形", en: "Polygon", Icon: Hexagon },
  { k: "dim", zh: "尺寸标注", en: "Dimension", Icon: Ruler },
  { k: "text", zh: "文字", en: "Text", Icon: Type },
  { k: "pen", zh: "手写画笔", en: "Pen", Icon: PenLine },
  { k: "eraser", zh: "橡皮擦", en: "Eraser", Icon: Eraser },
  { k: "crop", zh: "裁剪底图", en: "Crop", Icon: Crop },
  { k: "scale", zh: "标定比例尺", en: "Calibrate", Icon: MoveDiagonal },
];

const STAMP_ICONS = [
  { k: "water", zh: "水表", en: "Water", glyph: "水", gEn: "W", color: "#0ea5e9" },
  { k: "gas", zh: "气表", en: "Gas", glyph: "气", gEn: "G", color: "#f59e0b" },
  { k: "electric", zh: "电表", en: "Electric", glyph: "电", gEn: "E", color: "#eab308" },
  { k: "pole", zh: "电线杆", en: "Pole", glyph: "杆", gEn: "P", color: "#78716c" },
  { k: "entrance", zh: "主入口", en: "Entry", glyph: "入", gEn: "D", color: "#16a34a" },
  { k: "septic", zh: "化粪池", en: "Septic", glyph: "化", gEn: "S", color: "#8b5cf6" },
  { k: "storm", zh: "雨水管", en: "Drain", glyph: "雨", gEn: "SD", color: "#3b82f6" },
  { k: "ac", zh: "空调外机", en: "AC Unit", glyph: "冷", gEn: "AC", color: "#06b6d4" },
  { k: "tree", zh: "树木", en: "Tree", glyph: "树", gEn: "T", color: "#22c55e" },
];

// ---- geometry / measurement ----
const rectNorm = (p) => ({ x: Math.min(p[0].x, p[1].x), y: Math.min(p[0].y, p[1].y), w: Math.abs(p[1].x - p[0].x), h: Math.abs(p[1].y - p[0].y) });
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const polyCentroid = (pts) => ({ x: pts.reduce((s, q) => s + q.x, 0) / pts.length, y: pts.reduce((s, q) => s + q.y, 0) / pts.length });
const polyAreaPx = (pts) => { let a = 0; for (let i = 0; i < pts.length; i++) { const j = (i + 1) % pts.length; a += pts[i].x * pts[j].y - pts[j].x * pts[i].y; } return Math.abs(a) / 2; };
const rgba = (hex, op) => { const n = parseInt((hex || "#ef4444").slice(1), 16); return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${op})`; };
const fmtFt = (v) => (v >= 1000 ? Math.round(v).toLocaleString() : v.toFixed(v < 20 ? 1 : 0));
const segLenFt = (a, b, fx, fy) => Math.hypot((b.x - a.x) * fx, (b.y - a.y) * fy);

function shapeAreaSf(s, fx, fy) {
  if (!fx || !fy) return 0;
  if (s.type === "rect") { const r = rectNorm(s.points); return r.w * fx * r.h * fy; }
  if (s.type === "polygon" && s.closed && s.points.length >= 3) return polyAreaPx(s.points) * fx * fy;
  return 0;
}
function shapeLabel(s, fx, fy, approx) {
  if (s.customLabel !== undefined && s.customLabel !== null) return s.customLabel;
  if (s.type === "pen" || s.type === "icon") return "";
  if (!fx || !fy) return "";
  const pre = approx ? "≈" : "";
  if (s.type === "line" || s.type === "dim") return `${pre}${fmtFt(segLenFt(s.points[0], s.points[1], fx, fy))} ft`;
  if (s.type === "rect") { const r = rectNorm(s.points); const base = `${pre}${fmtFt(r.w * fx)}×${fmtFt(r.h * fy)} ft · ${Math.round(shapeAreaSf(s, fx, fy)).toLocaleString()} sf`; return s.labelTag ? `${s.labelTag} ${base}` : base; }
  if (s.type === "polygon" && s.closed) {
    const sf = Math.round(shapeAreaSf(s, fx, fy)).toLocaleString();
    if (s.labelTag) {
      const xs = s.points.map((q) => q.x), ys = s.points.map((q) => q.y);
      const w = (Math.max(...xs) - Math.min(...xs)) * fx, l = (Math.max(...ys) - Math.min(...ys)) * fy;
      return `${s.labelTag} ${pre}${sf} sf (${fmtFt(w)}×${fmtFt(l)} ft)`;
    }
    return `${pre}${sf} sf`;
  }
  return "";
}
function planDims(p) {
  const fx = p.ftuX || p.ftPerUnit || 0, fy = p.ftuY || p.ftPerUnit || (p.ftuX || p.ftPerUnit) || 0;
  if (!fx || !fy) return null;
  let best = null, bestA = 0;
  for (const s of (p.shapes || [])) { const a = shapeAreaSf(s, fx, fy); if (a > bestA) { bestA = a; best = s; } }
  if (!best) return null;
  let w = 0, l = 0;
  if (best.type === "rect") { const r = rectNorm(best.points); w = r.w * fx; l = r.h * fy; }
  else { const xs = best.points.map((q) => q.x), ys = best.points.map((q) => q.y); w = (Math.max(...xs) - Math.min(...xs)) * fx; l = (Math.max(...ys) - Math.min(...ys)) * fy; }
  return { w, l, sf: Math.round(bestA) };
}
function labelPos(s) {
  if (s.type === "line" || s.type === "dim") return { x: (s.points[0].x + s.points[1].x) / 2, y: (s.points[0].y + s.points[1].y) / 2 - 8 };
  if (s.type === "rect") { const r = rectNorm(s.points); return { x: r.x + r.w / 2, y: r.y + r.h / 2 }; }
  if (s.type === "polygon") return polyCentroid(s.points);
  if (s.type === "pen") return s.points[Math.floor(s.points.length / 2)];
  return s.points[0];
}
const bboxCenter = (pts) => { const xs = pts.map((q) => q.x), ys = pts.map((q) => q.y); return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 }; };
const cleanPoly = (pts) => pts.filter((q, i, arr) => i === 0 || dist(q, arr[i - 1]) > 2);
// 手写笔迹平滑: 两遍加权滑动平均, 保留首尾点, 快速手绘不抖
const smoothPen = (pts) => {
  if (!pts || pts.length < 5) return pts;
  let out = pts;
  for (let pass = 0; pass < 2; pass++) {
    const nx = [out[0]];
    for (let i = 1; i < out.length - 1; i++) nx.push({ x: (out[i - 1].x + out[i].x * 2 + out[i + 1].x) / 4, y: (out[i - 1].y + out[i].y * 2 + out[i + 1].y) / 4 });
    nx.push(out[out.length - 1]);
    out = nx;
  }
  return out;
};
// 各边小字长度标注; 相同且平行(对应)的边只标一条
function edgeLabels(s, fx, fy) {
  if (!fx || !fy) return [];
  let pts = null;
  if (s.type === "rect") { const r = rectNorm(s.points); pts = [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }]; }
  else if (s.type === "polygon" && s.closed && s.points.length >= 3) pts = s.points;
  else return [];
  const c = polyCentroid(pts);
  const shown = [], out = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    if (L < 26) continue;
    const len = segLenFt(a, b, fx, fy);
    const ux = (b.x - a.x) / L, uy = (b.y - a.y) / L;
    if (shown.some((e) => Math.abs(e.len - len) < Math.max(0.6, len * 0.02) && Math.abs(e.ux * ux + e.uy * uy) > 0.985)) continue;
    shown.push({ len, ux, uy });
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    let nx = -uy, ny = ux;
    if ((mx - c.x) * nx + (my - c.y) * ny < 0) { nx = -nx; ny = -ny; }
    out.push({ x: mx + nx * 10, y: my + ny * 10 + 3, text: `${fmtFt(len)}′` });
  }
  return out;
}
const rotPt = (p, deg, w, h) => { const r = deg * Math.PI / 180, cx = w / 2, cy = h / 2, dx = p.x - cx, dy = p.y - cy; return { x: cx + dx * Math.cos(r) - dy * Math.sin(r), y: cy + dx * Math.sin(r) + dy * Math.cos(r) }; };
const closestPtOnSeg = (p, a, b) => { const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy; if (!l2) return { x: a.x, y: a.y }; let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2; t = Math.max(0, Math.min(1, t)); return { x: a.x + t * dx, y: a.y + t * dy }; };
const pointInPoly = (p, pts) => { let inside = false; for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) { const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y; if (((yi > p.y) !== (yj > p.y)) && (p.x < (xj - xi) * (p.y - yi) / (yj - yi) + xi)) inside = !inside; } return inside; };
const distToSeg = (p, a, b) => { const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy; if (!l2) return dist(p, a); let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2; t = Math.max(0, Math.min(1, t)); return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy)); };
const hitShape = (s, p) => {
  if (s.type === "icon") return dist(s.points[0], p) < 11 * (s.iconScale || 1) + 6;
  if ((s.points || []).some((q) => dist(q, p) < 16)) return true;
  if (s.type === "rect") { const r = rectNorm(s.points); return p.x >= r.x - 6 && p.x <= r.x + r.w + 6 && p.y >= r.y - 6 && p.y <= r.y + r.h + 6; }
  if (s.type === "polygon" && s.closed && s.points.length >= 3) return pointInPoly(p, s.points);
  if (s.type === "line" || s.type === "dim") return distToSeg(p, s.points[0], s.points[1]) < 10;
  if (s.type === "polygon" || s.type === "pen") { for (let i = 0; i < s.points.length - 1; i++) if (distToSeg(p, s.points[i], s.points[i + 1]) < 10) return true; return false; }
  return false;
};

const niceNum = (v) => { if (v <= 0) return 1; const p = Math.pow(10, Math.floor(Math.log10(v))); const f = v / p; return (f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10) * p; };
const northAngle = (heading) => (-90 - (heading || 0)) * Math.PI / 180;
const scaleBar = (fx) => { if (!fx) return null; const ft = niceNum(100 * fx); return { ft, px: ft / fx }; };

function drawCompassCanvas(ctx, heading, w) {
  const cx = w - 46, cy = 46, r = 18, a = northAngle(heading);
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.82)"; ctx.beginPath(); ctx.arc(cx, cy, r + 5, 0, 7); ctx.fill();
  ctx.strokeStyle = "#334155"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(cx - Math.cos(a) * r, cy - Math.sin(a) * r); ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r); ctx.stroke();
  ctx.fillStyle = "#ef4444"; ctx.beginPath();
  ctx.moveTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  ctx.lineTo(cx + Math.cos(a + 2.7) * (r * 0.55), cy + Math.sin(a + 2.7) * (r * 0.55));
  ctx.lineTo(cx + Math.cos(a - 2.7) * (r * 0.55), cy + Math.sin(a - 2.7) * (r * 0.55));
  ctx.closePath(); ctx.fill();
  ctx.fillStyle = "#111827"; ctx.font = "bold 12px sans-serif"; ctx.textAlign = "center";
  ctx.fillText("N", cx + Math.cos(a) * (r + 9), cy + Math.sin(a) * (r + 9) + 4);
  ctx.textAlign = "left"; ctx.restore();
}
function drawScaleBarCanvas(ctx, fx, h) {
  const sb = scaleBar(fx); if (!sb) return;
  const x0 = 18, y0 = h - 24;
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.82)"; ctx.fillRect(x0 - 6, y0 - 18, sb.px + 14, 28);
  ctx.strokeStyle = "#111827"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x0 + sb.px, y0);
  ctx.moveTo(x0, y0 - 5); ctx.lineTo(x0, y0 + 5); ctx.moveTo(x0 + sb.px, y0 - 5); ctx.lineTo(x0 + sb.px, y0 + 5); ctx.stroke();
  ctx.fillStyle = "#111827"; ctx.font = "bold 11px sans-serif"; ctx.fillText(`${sb.ft} ft`, x0, y0 - 6);
  ctx.restore();
}

export function LayoutDrawEditor({ open, onClose, api, zh, leadId, onSaved }) {
  const { isLoaded, loadError, errorCode, mapId } = useGMaps();
  const [stage, setStage] = useState("frame");
  const [center, setCenter] = useState(null);
  const [zoom, setZoom] = useState(20);
  const [is3D, setIs3D] = useState(false);
  const [heading, setHeading] = useState(0);
  const [mapObj, setMapObj] = useState(null);
  const [base, setBase] = useState(null);
  const [cw, setCw] = useState(BASE_W);
  const [ch, setCh] = useState(BASE_H);
  const [ftuX, setFtuX] = useState(0);
  const [ftuY, setFtuY] = useState(0);
  const [approx, setApprox] = useState(false);
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [tool, setTool] = useState("select");
  const [color, setColor] = useState("#ef4444");
  const [fillOpacity, setFillOpacity] = useState(0.25);
  const [shapes, setShapes] = useState([]);
  const [draft, setDraft] = useState(null);
  const [selId, setSelId] = useState(null);
  const [name, setName] = useState("");
  const [perSf, setPerSf] = useState("");
  const [ownerNotes, setOwnerNotes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [renderingId, setRenderingId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchProg, setBatchProg] = useState(null);
  const [rotPreview, setRotPreview] = useState(0);
  const [hoverPt, setHoverPt] = useState(null);
  const [cropRect, setCropRect] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [zoomPlan, setZoomPlan] = useState(null);
  const [histTick, setHistTick] = useState(0);
  const svgRef = useRef(null);
  const [labelEdit, setLabelEdit] = useState(null);
  const [textEdit, setTextEdit] = useState(null);
  const [snapInd, setSnapInd] = useState(null);
  const [penWidth, setPenWidth] = useState(4);
  const [penOpacity, setPenOpacity] = useState(90);
  const [eraserR, setEraserR] = useState(14);
  const [eraserPt, setEraserPt] = useState(null);
  const [rotLive, setRotLive] = useState(null);
  const [aiBeds, setAiBeds] = useState("auto");
  const [aiBaths, setAiBaths] = useState("auto");
  const [aiOrient, setAiOrient] = useState("auto");
  const [customOpen, setCustomOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customColor, setCustomColor] = useState("#0ea5e9");
  const [myIcons, setMyIcons] = useState([]);
  const imgRef = useRef(null);
  const mapWrapRef = useRef(null);
  const dragRef = useRef(null);
  const histRef = useRef({ past: [], future: [] });
  const [parcel, setParcel] = useState(null);
  const [showParcel, setShowParcel] = useState(true);
  const [showSetback, setShowSetback] = useState(true);
  const [parcelMsg, setParcelMsg] = useState("");
  const fitDoneRef = useRef(false);
  const [guideZone, setGuideZone] = useState(null);
  const [buildings, setBuildings] = useState(null);
  const [showBuildings, setShowBuildings] = useState(true);
  const [bldZones, setBldZones] = useState(null);
  const [slope, setSlope] = useState(null);
  const [fixMode, setFixMode] = useState(false);
  const [cand, setCand] = useState(null);
  const [candBusy, setCandBusy] = useState(false);
  const [contours, setContours] = useState(null);
  const [showContours, setShowContours] = useState(false);
  const [contourBusy, setContourBusy] = useState(false);
  const [bldTried, setBldTried] = useState(false);
  const [bldBusy, setBldBusy] = useState(false);
  const setbackCoords = React.useMemo(() => (parcel ? insetPolygon(parcel.coords, 4) : null), [parcel]);
  const setbackSqft = React.useMemo(() => (setbackCoords ? Math.round(polyAreaSqft(setbackCoords)) : 0), [setbackCoords]);

  // ADU 与现有建筑/退界避让: 重叠/间距不足/超出可建区实时警示(移动/旋转/缩放即重算)
  const aduConflicts = React.useMemo(() => {
    const hasBld = bldZones && bldZones.length;
    const hasZone = guideZone && guideZone.length >= 3;
    if (!hasBld && !hasZone) return [];
    const fAvg = ftuX > 0 ? (ftuX + (ftuY || ftuX)) / 2 : 0;
    const out = [];
    for (const s of shapes) {
      const corners = aduCorners(s);
      if (!corners) continue;
      const hitIdx = [], nearIdx = [];
      let minFt = Infinity;
      (bldZones || []).forEach((bz, bi) => {
        if (!bz || bz.length < 3) return;
        if (polysOverlap(corners, bz)) { hitIdx.push(bi); return; }
        if (fAvg > 0) {
          const dFt = polyMinDist(corners, bz) * fAvg;
          if (dFt < MIN_BLD_CLEAR_FT) { nearIdx.push(bi); minFt = Math.min(minFt, dFt); }
        }
      });
      if (hitIdx.length) out.push({ id: s.id, kind: "overlap", bldIdx: hitIdx, corners });
      else if (nearIdx.length) out.push({ id: s.id, kind: "near", bldIdx: nearIdx, corners, distFt: minFt });
      if (hasZone && corners.some((p) => !pointInPoly(p, guideZone))) {
        out.push({ id: s.id, kind: "setback", bldIdx: [], corners });
      }
    }
    return out;
  }, [shapes, bldZones, guideZone, ftuX, ftuY]);

  // ADU 与树木冠幅间距提示(琥珀色, 仅提醒)
  const treeHints = React.useMemo(() => {
    const fAvg = ftuX > 0 ? (ftuX + (ftuY || ftuX)) / 2 : 0;
    if (!fAvg) return [];
    const trees = shapes.filter((s) => s.type === "icon" && s.icon === "tree" && s.canopyFt > 0);
    if (!trees.length) return [];
    const out = [];
    for (const s of shapes) {
      const corners = aduCorners(s);
      if (!corners) continue;
      for (const t of trees) {
        const c0 = t.points[0];
        let dPx = pointInPoly(c0, corners) ? 0 : Infinity;
        if (dPx > 0) for (let i = 0; i < corners.length; i++) dPx = Math.min(dPx, distToSeg(c0, corners[i], corners[(i + 1) % corners.length]));
        const dFt = dPx * fAvg;
        if (dFt < t.canopyFt) out.push({ aduId: s.id, treeId: t.id, distFt: dFt, canopyFt: t.canopyFt });
      }
    }
    return out;
  }, [shapes, ftuX, ftuY]);

  // 公用设施(水/气/电/杆/化粪池/雨水)到最近 ADU 的距离标注 + 主入口步道路径线, 含接管/铺装费用初步估算
  const utilLinks = React.useMemo(() => {
    const fAvg = ftuX > 0 ? (ftuX + (ftuY || ftuX)) / 2 : 0;
    if (!fAvg) return [];
    const adus = shapes.map((s) => aduCorners(s)).filter(Boolean);
    if (!adus.length) return [];
    const out = [];
    for (const s of shapes) {
      if (s.type !== "icon" || !LINK_ICON_KEYS.includes(s.icon)) continue;
      const c0 = s.points[0];
      let best = null, bd = Infinity;
      for (const corners of adus) {
        if (pointInPoly(c0, corners)) { best = c0; bd = 0; break; }
        for (let i = 0; i < corners.length; i++) {
          const q = closestPtOnSeg(c0, corners[i], corners[(i + 1) % corners.length]);
          const d = dist(c0, q);
          if (d < bd) { bd = d; best = q; }
        }
      }
      const distFt = bd * fAvg;
      if (!best || distFt < 1) continue;
      const cost = UTIL_COST_FT[s.icon];
      out.push({
        id: s.id, icon: s.icon, kind: s.icon === "entrance" ? "path" : "util",
        label: s.iconLabel || s.icon, from: c0, to: best, distFt, color: s.color,
        low: round50(distFt * cost[0]), high: round50(distFt * cost[1]),
      });
    }
    return out;
  }, [shapes, ftuX, ftuY]);

  // 坡向/排水方向箭头(取景阶段): 高→低, 与等高线一起显示并进入截图
  const flowArrow = React.useMemo(() => {
    const f = contours?.flow;
    if (!f || !showContours) return null;
    let clat, clng, spanFt;
    const pc = parcel?.coords;
    if (pc && pc.length >= 3) {
      clat = pc.reduce((s, c) => s + c[0], 0) / pc.length;
      clng = pc.reduce((s, c) => s + c[1], 0) / pc.length;
      const las = pc.map((c) => c[0]), lgs2 = pc.map((c) => c[1]);
      spanFt = Math.max((Math.max(...las) - Math.min(...las)) * 364567, (Math.max(...lgs2) - Math.min(...lgs2)) * 365221 * Math.cos((clat * Math.PI) / 180));
    } else if (center) {
      clat = Number(center.lat); clng = Number(center.lng); spanFt = 250;
    } else return null;
    const halfFt = Math.max(spanFt * 0.28, 40);
    const rad = (f.bearingDeg * Math.PI) / 180;
    const dLat = (Math.cos(rad) * halfFt) / 364567;
    const dLng = (Math.sin(rad) * halfFt) / (365221 * Math.cos((clat * Math.PI) / 180));
    return { from: { lat: clat - dLat, lng: clng - dLng }, to: { lat: clat + dLat, lng: clng + dLng }, mid: { lat: clat, lng: clng } };
  }, [contours, showContours, parcel, center]);

  const loadPlans = useCallback(() => {
    api.get(`/crm/leads/${leadId}/layout-plans`).then(({ data }) => setPlans(data || [])).catch(() => {});
  }, [api, leadId]);

  const resetDraw = () => { setShapes([]); setDraft(null); setSelId(null); setName(""); setPerSf(""); setOwnerNotes([]); setEditingPlanId(null); setBase(null); setCw(BASE_W); setCh(BASE_H); setFtuX(0); setFtuY(0); setApprox(false); histRef.current = { past: [], future: [] }; };

  useEffect(() => {
    if (!open) return;
    setStage("frame"); setFullscreen(true); resetDraw();
    api.get(`/crm/leads/${leadId}/site-layouts`).then(({ data }) => {
      setCenter(data.lat && data.lng ? { lat: Number(data.lat), lng: Number(data.lng) } : { lat: 34.0522, lng: -118.2437 });
      if (data.layoutPrefs?.contours) fetchContours(true);
    }).catch(() => setCenter({ lat: 34.0522, lng: -118.2437 }));
    // 物业地界线：按地址自动查询县级宗地红线
    setParcel(null); setParcelMsg(""); setShowParcel(true); setShowSetback(true); fitDoneRef.current = false;
    setGuideZone(null); setSlope(null); setBuildings(null); setBldZones(null);
    setFixMode(false); setCand(null);
    setContours(null); setShowContours(false); setContourBusy(false); setBldTried(false); setBldBusy(false);
    api.get("/me/custom-icons").then(({ data }) => setMyIcons(data.icons || [])).catch(() => {});
    api.get(`/crm/leads/${leadId}/buildings`).then(({ data }) => {
      if (data.found && (data.polys || []).length) setBuildings(data.polys);
      setBldTried(true);
    }).catch(() => setBldTried(true));
    api.get(`/crm/leads/${leadId}/parcel`).then(({ data }) => {
      if (data.found && (data.coords || []).length >= 3) {
        setParcel(data);
        if (data.slope) setSlope(data.slope);
      } else {
        setParcelMsg(data.reason === "no_coords" ? (zh ? "地址未能定位，无法自动标注地界线" : "Address not geocoded — no parcel line") : (zh ? "该县暂不支持自动地界线（已支持：洛杉矶/圣地亚哥/Santa Clara/Alameda 等）" : "Parcel data not available for this county"));
      }
    }).catch(() => setParcelMsg(zh ? "地界线查询失败" : "Parcel lookup failed"));
    loadPlans();
  }, [open, leadId]); // eslint-disable-line

  // 地块自动取景：地界线加载后自动缩放居中至地块范围
  useEffect(() => {
    if (!open || !mapObj || !parcel || fitDoneRef.current || stage !== "frame") return;
    try {
      const b = new window.google.maps.LatLngBounds();
      (parcel.coords || []).forEach((c) => b.extend({ lat: c[0], lng: c[1] }));
      mapObj.fitBounds(b, 70);
      fitDoneRef.current = true;
    } catch { /* ignore */ }
  }, [open, mapObj, parcel, stage]);

  // 地块坡度提示：后端 USGS NED10m 采样(随 parcel 返回), 此处为兜底重取
  useEffect(() => {
    if (!open || !parcel || slope) return;
    if (parcel.slope) setSlope(parcel.slope);
  }, [open, parcel, slope]);

  // ---- history ----
  const snap = () => ({ shapes: JSON.parse(JSON.stringify(shapes)), base, cw, ch, ftuX, ftuY, approx });
  const pushHist = (s) => { const h = histRef.current; h.past.push(s || snap()); if (h.past.length > 40) h.past.shift(); h.future = []; setHistTick((t) => t + 1); };
  const applySnap = (s) => { setShapes(s.shapes); setBase(s.base); setCw(s.cw ?? BASE_W); setCh(s.ch ?? BASE_H); setFtuX(s.ftuX); setFtuY(s.ftuY); setApprox(s.approx); setSelId(null); setDraft(null); };
  const undo = () => { const h = histRef.current; if (!h.past.length) return; h.future.push(snap()); applySnap(h.past.pop()); setHistTick((t) => t + 1); };
  const redo = () => { const h = histRef.current; if (!h.future.length) return; h.past.push(snap()); applySnap(h.future.pop()); setHistTick((t) => t + 1); };
  const canUndo = histRef.current.past.length > 0;
  const canRedo = histRef.current.future.length > 0;

  const toggle3D = () => { const next = !is3D; setIs3D(next); if (mapObj) mapObj.setTilt(next ? 47.5 : 0); };

  // ---- 等高线: 首次开启时按需拉取(USGS NED10m 网格 + 后端 marching squares, 带缓存); 开关状态记忆到 lead ----
  const fetchContours = async (auto = false) => {
    setContourBusy(true);
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/contours`);
      if (data.found) {
        setContours(data); setShowContours(true);
        if (!auto && !(data.lines || []).length) toast.info(zh ? `地势平坦（高差仅 ${(data.maxFt - data.minFt).toFixed(1)} ft），无可绘等高线` : "Site is flat — no contours to draw");
      } else if (!auto) {
        toast.error(data.reason === "no_coords" ? (zh ? "地址未定位，无法生成等高线" : "No coordinates") : (zh ? "高程数据获取失败，请稍后重试" : "Elevation data unavailable"));
      }
    } catch { if (!auto) toast.error(zh ? "等高线生成失败" : "Contours failed"); }
    setContourBusy(false);
  };
  const saveContoursPref = (v) => { api.post(`/crm/leads/${leadId}/layout-prefs`, { contours: v }).catch(() => {}); };
  const toggleContours = async () => {
    if (showContours) { setShowContours(false); saveContoursPref(false); return; }
    saveContoursPref(true);
    if (contours) { setShowContours(true); return; }
    await fetchContours(false);
  };

  // ---- 建筑轮廓: 未识别时手动重试(县级GIS + OSM兜底) ----
  const refetchBuildings = async () => {
    setBldBusy(true);
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/buildings`, { params: { refresh: true } });
      if (data.found && (data.polys || []).length) {
        setBuildings(data.polys); setShowBuildings(true);
        toast.success(zh ? `已自动识别 ${data.polys.length} 处现有建筑轮廓（${data.source}）` : `${data.polys.length} buildings found`);
      } else {
        toast.error(zh ? "该区域县级GIS与OSM均无建筑轮廓数据" : "No building footprint data here");
      }
    } catch { toast.error(zh ? "建筑轮廓识别失败" : "Building lookup failed"); }
    setBldBusy(false);
  };

  // ---- 地块识别纠错: 点击相邻地块预览红线并可替换 ----
  const onMapPick = async (e) => {
    if (!fixMode || candBusy || !e.latLng) return;
    const la = e.latLng.lat(), lg = e.latLng.lng();
    setCandBusy(true);
    try {
      const { data } = await api.get(`/crm/leads/${leadId}/parcel/preview`, { params: { lat: la, lng: lg } });
      if (data.found) setCand(data);
      else toast.error(zh ? "该位置未查到地块，请点击地块内部再试" : "No parcel found here — click inside a parcel");
    } catch { toast.error(zh ? "地块查询失败" : "Parcel lookup failed"); }
    setCandBusy(false);
  };
  const useCand = async () => {
    if (!cand) return;
    setCandBusy(true);
    try {
      const { data } = await api.post(`/crm/leads/${leadId}/parcel/select`, { lat: cand.clickLat, lng: cand.clickLng });
      setParcel(data); setSlope(data.slope || null); setCand(null); setFixMode(false);
      fitDoneRef.current = false;
      toast.success(zh ? "已更正地块红线" : "Parcel corrected");
    } catch (e2) { toast.error(e2?.response?.data?.detail || (zh ? "更正失败" : "Failed")); }
    setCandBusy(false);
  };

  const ftuFor = (map, wCss, hCss) => {
    const lat = map.getCenter().lat();
    const z = map.getZoom();
    const mPerPx = 156543.03392 * Math.cos((lat * Math.PI) / 180) / Math.pow(2, z);
    return { fx: (wCss * mPerPx * M2FT) / BASE_W, fy: (hCss * mPerPx * M2FT) / BASE_H };
  };

  // 把经纬度多边形投影到截取后底图的逻辑坐标(仅 2D 正北视角)
  const projectPolyToLogical = (coords, map, ncw, nch) => {
    try {
      if (!coords || coords.length < 3 || is3D || (map.getHeading && map.getHeading())) return null;
      const b = map.getBounds();
      if (!b) return null;
      const ne = b.getNorthEast(), sw = b.getSouthWest();
      const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
      const west = sw.lng(), east = ne.lng();
      const top = mercY(ne.lat()), bot = mercY(sw.lat());
      if (east <= west || top <= bot) return null;
      const pts = coords.map(([la, lg]) => ({
        x: ((lg - west) / (east - west)) * ncw,
        y: ((top - mercY(la)) / (top - bot)) * nch,
      }));
      return pts.some((p) => !isFinite(p.x) || !isFinite(p.y)) ? null : pts;
    } catch { return null; }
  };
  const projectSetbackToLogical = (map, ncw, nch) => projectPolyToLogical(setbackCoords, map, ncw, nch);
  const projectBuildings = (map, ncw, nch) =>
    (buildings || []).map((poly) => projectPolyToLogical(poly, map, ncw, nch)).filter(Boolean);

  // ADU 摆放模板: 按真实英尺生成, 自动放入可建参考区中心并旋转对齐地块朝向
  const addAduPreset = (sf, wft, hft) => {
    if (!ftuX) { toast.error(zh ? "当前底图无比例尺，请先「标定比例尺」再用模板" : "Calibrate scale first"); return; }
    const wpx = wft / ftuX, hpx = hft / (ftuY || ftuX);
    let cx = cw / 2, cy = ch / 2, angle = 0;
    if (guideZone && guideZone.length >= 3) {
      cx = guideZone.reduce((s, p) => s + p.x, 0) / guideZone.length;
      cy = guideZone.reduce((s, p) => s + p.y, 0) / guideZone.length;
      let bl = -1;
      for (let i = 0; i < guideZone.length; i++) {
        const a = guideZone[i], b = guideZone[(i + 1) % guideZone.length];
        const l = Math.hypot(b.x - a.x, b.y - a.y);
        if (l > bl) { bl = l; angle = Math.atan2(b.y - a.y, b.x - a.x); }
      }
      const xs = guideZone.map((p) => p.x), ys = guideZone.map((p) => p.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
      const halfS = Math.min(wpx, hpx) / 2;
      if (maxX - minX > halfS * 2) cx = Math.min(Math.max(cx, minX + halfS), maxX - halfS);
      if (maxY - minY > halfS * 2) cy = Math.min(Math.max(cy, minY + halfS), maxY - halfS);
    }
    let corners;
    if (Math.abs(angle) < 0.02 || Math.abs(Math.abs(angle) - Math.PI) < 0.02) {
      corners = [{ x: cx - wpx / 2, y: cy - hpx / 2 }, { x: cx + wpx / 2, y: cy - hpx / 2 },
                 { x: cx + wpx / 2, y: cy + hpx / 2 }, { x: cx - wpx / 2, y: cy + hpx / 2 }];
      addShape({ type: "rect", points: [{ x: cx - wpx / 2, y: cy - hpx / 2 }, { x: cx + wpx / 2, y: cy + hpx / 2 }], labelTag: "ADU" });
    } else {
      const halfL = Math.max(wpx, hpx) / 2, halfS2 = Math.min(wpx, hpx) / 2;
      const ca = Math.cos(angle), sa = Math.sin(angle);
      corners = [[-halfL, -halfS2], [halfL, -halfS2], [halfL, halfS2], [-halfL, halfS2]]
        .map(([dx, dy]) => ({ x: cx + dx * ca - dy * sa, y: cy + dx * sa + dy * ca }));
      addShape({ type: "polygon", points: corners, closed: true, labelTag: "ADU" });
    }
    const hitBld = (bldZones || []).some((bz) => polysOverlap(corners, bz));
    if (hitBld) {
      toast.warning(zh ? "⚠ 模板与现有建筑（主屋/车库）轮廓重叠，请拖动避开后再定稿" : "⚠ Preset overlaps an existing building — drag it clear");
    } else {
      toast.success(zh
        ? (guideZone ? `ADU ${sf} sf 已放入可建参考区并对齐地块朝向，可拖动微调` : `ADU ${sf} sf 模板已添加，可拖动摆放`)
        : `ADU ${sf} sf added`);
    }
  };

  const enterDraw = (src, fx, fy, isApprox, ncw = BASE_W, nch = BASE_H) => {
    setBase(src); setCw(ncw); setCh(nch); setFtuX(fx || 0); setFtuY(fy || 0); setApprox(!!isApprox);
    setShapes([]); setDraft(null); setSelId(null); setEditingPlanId(null); setOwnerNotes([]); setTool("select");
    setRotPreview(0); setHoverPt(null); setCropRect(null);
    histRef.current = { past: [], future: [] };
    setStage("draw");
  };

  // ---- capture ----
  const captureScreen = async () => {
    if (!mapObj || !mapWrapRef.current) return;
    setCapturing(true);
    const c = mapObj.getCenter(); setCenter({ lat: c.lat(), lng: c.lng() }); setZoom(mapObj.getZoom());
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: "browser" }, preferCurrentTab: true, audio: false });
      const track = stream.getVideoTracks()[0];
      const video = document.createElement("video"); video.srcObject = stream; video.muted = true;
      await video.play(); await new Promise((r) => setTimeout(r, 350));
      const vw = video.videoWidth, vh = video.videoHeight;
      const rect = mapWrapRef.current.getBoundingClientRect();
      const sx = vw / window.innerWidth, sy = vh / window.innerHeight;
      const outW = Math.round(rect.width * sx), outH = Math.round(rect.height * sy);
      const cv = document.createElement("canvas"); cv.width = outW; cv.height = outH;
      cv.getContext("2d").drawImage(video, rect.left * sx, rect.top * sy, rect.width * sx, rect.height * sy, 0, 0, outW, outH);
      track.stop(); stream.getTracks().forEach((t) => t.stop());
      const { fx, fy } = ftuFor(mapObj, rect.width, rect.height);
      enterDraw(cv.toDataURL("image/png"), fx, fy, is3D);
      setGuideZone(projectSetbackToLogical(mapObj, BASE_W, BASE_H));
      setBldZones(projectBuildings(mapObj, BASE_W, BASE_H));
      toast.success(zh ? "已截取当前 Google 地图视角" : "Captured current Google map view");
    } catch (e) {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      toast.error(zh ? "屏幕截图被取消或不被允许，请重试并在授权框选择「当前标签页」" : "Screen capture unavailable — retry and pick this tab");
    }
    setCapturing(false);
  };



  // ---- coordinate helpers ----
  const toPt = (e) => {
    const svg = svgRef.current;
    const cx = (e.touches ? e.touches[0].clientX : e.clientX);
    const cy = (e.touches ? e.touches[0].clientY : e.clientY);
    const ctm = svg.getScreenCTM && svg.getScreenCTM();
    if (ctm) {
      const sp = svg.createSVGPoint(); sp.x = cx; sp.y = cy;
      const p = sp.matrixTransform(ctm.inverse());
      return { x: Math.max(0, Math.min(cw, p.x)), y: Math.max(0, Math.min(ch, p.y)) };
    }
    const r = svg.getBoundingClientRect();
    return { x: Math.max(0, Math.min(cw, (cx - r.left) / r.width * cw)), y: Math.max(0, Math.min(ch, (cy - r.top) / r.height * ch)) };
  };
  const snapCandidates = (skipId) => {
    const out = [];
    for (const s of shapes) {
      if (s.id === skipId || s.type === "text") continue;
      if (s.type === "rect") {
        const r = rectNorm(s.points);
        const c = [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }];
        out.push(...c);
        c.forEach((q, i) => { const n = c[(i + 1) % 4]; out.push({ x: (q.x + n.x) / 2, y: (q.y + n.y) / 2 }); });
      } else if (s.type === "polygon") {
        out.push(...s.points);
        if (s.closed) s.points.forEach((q, i) => { const n = s.points[(i + 1) % s.points.length]; out.push({ x: (q.x + n.x) / 2, y: (q.y + n.y) / 2 }); });
      } else if (s.type === "pen") { out.push(s.points[0], s.points[s.points.length - 1]); }
      else out.push(...(s.points || []));
    }
    return out;
  };
  const snapVertexInfo = (p, skipId, radius = SNAP) => {
    let best = null, bd = radius;
    for (const q of snapCandidates(skipId)) { const d = dist(q, p); if (d < bd) { bd = d; best = q; } }
    return best ? { pt: { x: best.x, y: best.y }, snapped: true } : { pt: p, snapped: false };
  };
  const snapVertex = (p, skipId, radius = SNAP) => snapVertexInfo(p, skipId, radius).pt;
  const orthoSnap = (anchor, p) => {
    const dx = Math.abs(p.x - anchor.x), dy = Math.abs(p.y - anchor.y);
    if (dy < ORTHO && dx >= dy) return { x: p.x, y: anchor.y };
    if (dx < ORTHO && dy > dx) return { x: anchor.x, y: p.y };
    return p;
  };
  const addShape = (sh) => { pushHist(); const id = `s${Date.now()}${Math.random().toString(36).slice(2, 5)}`; setShapes((a) => [...a, { id, color, fillOpacity, ...sh }]); setSelId(id); if (sh.type !== "pen") setTool("select"); };
  const updSel = (patch) => { if (!selId) return; pushHist(); setShapes((a) => a.map((s) => s.id === selId ? { ...s, ...patch } : s)); };
  const setSelLabel = (val) => { if (!selId) return; setShapes((a) => a.map((s) => s.id === selId ? { ...s, customLabel: val } : s)); };
  const resetSelLabel = () => { if (!selId) return; setShapes((a) => a.map((s) => { if (s.id !== selId) return s; const { customLabel, ...rest } = s; return rest; })); };
  const delSel = () => { if (!selId) return; pushHist(); setShapes((a) => a.filter((s) => s.id !== selId)); setSelId(null); };
  const selShape = shapes.find((s) => s.id === selId);
  const setIconScale = (v) => { if (!selId) return; setShapes((a) => a.map((s) => s.id === selId ? { ...s, iconScale: v } : s)); };
  // 橡皮擦: 仅擦除手写笔迹局部, 中段被擦除时自动断成两段
  const eraseAt = (p, r) => {
    setShapes((arr) => {
      let changedAll = false;
      const out = [];
      for (const s of arr) {
        if (s.type !== "pen") { out.push(s); continue; }
        let erased = false;
        const runs = [];
        let cur = [];
        for (const q of s.points) {
          if (dist(q, p) <= r) { erased = true; if (cur.length) { runs.push(cur); cur = []; } }
          else cur.push(q);
        }
        if (cur.length) runs.push(cur);
        if (!erased) { out.push(s); continue; }
        changedAll = true;
        runs.filter((r2) => r2.length >= 2).forEach((r2, i) => out.push({ ...s, id: i === 0 ? s.id : `${s.id}e${i}_${Math.round(r2[0].x)}_${Math.round(r2[0].y)}`, points: r2 }));
      }
      return changedAll ? out : arr;
    });
  };

  const handlesOf = (s) => {
    if (!s) return [];
    if (s.type === "pen" || s.type === "icon") return [];
    if (s.type === "line" || s.type === "dim") return [{ k: "p0", pt: s.points[0] }, { k: "p1", pt: s.points[1] }];
    if (s.type === "rect") { const a = s.points[0], b = s.points[1]; return [{ k: "a", pt: a }, { k: "b", pt: b }, { k: "c1", pt: { x: b.x, y: a.y } }, { k: "c2", pt: { x: a.x, y: b.y } }]; }
    if (s.type === "polygon") return s.points.map((pt, i) => ({ k: "v" + i, pt }));
    return [{ k: "p0", pt: s.points[0] }];
  };
  // 旋转手柄：包围盒四角外侧
  const rotHandlesOf = (s) => {
    if (!s || s.type === "text" || s.type === "icon" || (s.points || []).length < 2) return [];
    const xs = s.points.map((q) => q.x), ys = s.points.map((q) => q.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const o = 17;
    return [
      { k: "r0", pt: { x: minX - o, y: minY - o } }, { k: "r1", pt: { x: maxX + o, y: minY - o } },
      { k: "r2", pt: { x: maxX + o, y: maxY + o } }, { k: "r3", pt: { x: minX - o, y: maxY + o } },
    ];
  };
  const applyHandle = (s, k, p) => {
    if (s.type === "line" || s.type === "dim") { const other = k === "p0" ? s.points[1] : s.points[0]; const np = orthoSnap(other, p); return { ...s, points: k === "p0" ? [np, s.points[1]] : [s.points[0], np] }; }
    if (s.type === "rect") { let a = { ...s.points[0] }, b = { ...s.points[1] };
      if (k === "a") a = p; else if (k === "b") b = p; else if (k === "c1") { a.y = p.y; b.x = p.x; } else { a.x = p.x; b.y = p.y; }
      return { ...s, points: [a, b] }; }
    if (s.type === "polygon") { const i = Number(k.slice(1)); return { ...s, points: s.points.map((q, j) => j === i ? p : q) }; }
    return { ...s, points: [p] };
  };

  const onDown = (e) => {
    if (stage !== "draw") return;
    const raw = toPt(e);
    if (tool === "select") {
      if (selShape) {
        const rh = rotHandlesOf(selShape).find((hh) => dist(hh.pt, raw) < HANDLE + 2);
        if (rh) {
          let sh = selShape;
          if (sh.type === "rect") {
            const r = rectNorm(sh.points);
            sh = { ...sh, type: "polygon", closed: true, points: [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }] };
          }
          const c = bboxCenter(sh.points);
          dragRef.current = { mode: "rotate", id: sh.id, center: c, startAng: Math.atan2(raw.y - c.y, raw.x - c.x), orig: sh.points.map((q) => ({ ...q })), before: snap(), moved: false };
          if (selShape.type === "rect") setShapes((a) => a.map((s) => s.id === selShape.id ? sh : s));
          return;
        }
        const h = handlesOf(selShape).find((hh) => dist(hh.pt, raw) < HANDLE);
        if (h) { dragRef.current = { mode: "handle", id: selShape.id, k: h.k, before: snap(), moved: false }; return; }
      }
      const hit = [...shapes].reverse().find((s) => hitShape(s, raw));
      if (hit) { setSelId(hit.id); dragRef.current = { mode: "move", id: hit.id, start: raw, orig: hit.points.map((q) => ({ ...q })), before: snap(), moved: false }; }
      else setSelId(null);
      return;
    }
    if (tool === "pen") { setDraft({ type: "pen", points: [raw] }); return; }
    if (tool === "eraser") { dragRef.current = { mode: "erase", before: snap(), moved: true }; setEraserPt(raw); eraseAt(raw, eraserR); return; }
    const si = snapVertexInfo(raw, undefined, tool === "dim" ? 18 : SNAP);
    const p = si.pt;
    setSnapInd(si.snapped ? si.pt : null);
    if (tool === "line" || tool === "rect" || tool === "dim") setDraft({ type: tool, points: [p, p] });
    else if (tool === "crop") {
      if (cropRect) {
        const h = cropHandles(cropRect).find((hh) => dist(hh.pt, raw) < HANDLE + 4);
        if (h) { dragRef.current = { mode: "crophandle", k: h.k, rect: { ...cropRect } }; return; }
        if (raw.x >= cropRect.x && raw.x <= cropRect.x + cropRect.w && raw.y >= cropRect.y && raw.y <= cropRect.y + cropRect.h) {
          dragRef.current = { mode: "cropmove", start: raw, rect: { ...cropRect } }; return;
        }
      }
      setDraft({ type: "crop", points: [raw, raw] });
    }
    else if (tool === "scale") setDraft({ type: "scale", points: [raw, raw] });
  };
  const onMove = (e) => {
    if (tool === "eraser") {
      const p = toPt(e);
      setEraserPt(p);
      if (dragRef.current && dragRef.current.mode === "erase") { eraseAt(p, eraserR); }
      if (dragRef.current && dragRef.current.mode === "erase") return;
    }
    if (dragRef.current && (dragRef.current.mode === "crophandle" || dragRef.current.mode === "cropmove")) {
      const d = dragRef.current; const cur = toPt(e);
      if (d.mode === "cropmove") { const dx = cur.x - d.start.x, dy = cur.y - d.start.y; setCropRect({ x: Math.max(0, Math.min(cw - d.rect.w, d.rect.x + dx)), y: Math.max(0, Math.min(ch - d.rect.h, d.rect.y + dy)), w: d.rect.w, h: d.rect.h }); }
      else setCropRect(applyCropHandle(d.rect, d.k, cur));
      return;
    }
    if (dragRef.current && dragRef.current.mode === "rotate") {
      const d = dragRef.current; d.moved = true;
      const cur = toPt(e);
      let ang = Math.atan2(cur.y - d.center.y, cur.x - d.center.x) - d.startAng;
      if (e.shiftKey) ang = Math.round(ang / (Math.PI / 12)) * (Math.PI / 12);
      const deg = ((Math.round(ang * 180 / Math.PI) % 360) + 540) % 360 - 180;
      setRotLive({ deg, x: d.center.x, y: d.center.y });
      const ca = Math.cos(ang), sa = Math.sin(ang);
      setShapes((a) => a.map((s) => s.id === d.id ? { ...s, points: d.orig.map((q) => ({ x: d.center.x + (q.x - d.center.x) * ca - (q.y - d.center.y) * sa, y: d.center.y + (q.x - d.center.x) * sa + (q.y - d.center.y) * ca })) } : s));
      return;
    }
    if (dragRef.current) {
      const d = dragRef.current; d.moved = true;
      const p = snapVertex(toPt(e), d.id);
      if (d.mode === "handle") setShapes((a) => a.map((s) => s.id === d.id ? applyHandle(s, d.k, p) : s));
      else { const cur = toPt(e); const dx = cur.x - d.start.x, dy = cur.y - d.start.y; setShapes((a) => a.map((s) => s.id === d.id ? { ...s, points: d.orig.map((q) => ({ x: q.x + dx, y: q.y + dy })) } : s)); }
      return;
    }
    if (!draft) return;
    if (draft.type === "pen") {
      const p = toPt(e);
      setDraft((dd) => { const last = dd.points[dd.points.length - 1]; return dist(last, p) > 2 ? { ...dd, points: [...dd.points, p] } : dd; });
      return;
    }
    if (draft.type === "polygon") { setHoverPt(snapVertex(toPt(e))); return; }
    let p = toPt(e);
    if (draft.type === "line" || draft.type === "dim") {
      const si = snapVertexInfo(p, undefined, draft.type === "dim" ? 18 : SNAP);
      setSnapInd(si.snapped ? si.pt : null);
      p = si.snapped ? si.pt : orthoSnap(draft.points[0], p);
    }
    setDraft((dd) => ({ ...dd, points: [dd.points[0], p] }));
  };
  const cropHandles = (r) => ([
    { k: "nw", pt: { x: r.x, y: r.y } }, { k: "n", pt: { x: r.x + r.w / 2, y: r.y } }, { k: "ne", pt: { x: r.x + r.w, y: r.y } },
    { k: "e", pt: { x: r.x + r.w, y: r.y + r.h / 2 } }, { k: "se", pt: { x: r.x + r.w, y: r.y + r.h } }, { k: "s", pt: { x: r.x + r.w / 2, y: r.y + r.h } },
    { k: "sw", pt: { x: r.x, y: r.y + r.h } }, { k: "w", pt: { x: r.x, y: r.y + r.h / 2 } },
  ]);
  const applyCropHandle = (r, k, raw) => {
    const p = { x: Math.max(0, Math.min(cw, raw.x)), y: Math.max(0, Math.min(ch, raw.y)) };
    let ax = r.x, ay = r.y, bx = r.x + r.w, by = r.y + r.h;
    if (k.includes("w")) ax = p.x; if (k.includes("e")) bx = p.x;
    if (k.includes("n")) ay = p.y; if (k.includes("s")) by = p.y;
    return { x: Math.min(ax, bx), y: Math.min(ay, by), w: Math.abs(bx - ax), h: Math.abs(by - ay) };
  };
  const applyCrop = (r) => {
    if (!imgRef.current || r.w < 20 || r.h < 20) { toast.error(zh ? "裁剪框太小" : "Crop too small"); return; }
    pushHist();
    const im = imgRef.current;
    const nw = im.naturalWidth, nh = im.naturalHeight;
    const sx = (r.x / cw) * nw, sy = (r.y / ch) * nh, sw = (r.w / cw) * nw, sh = (r.h / ch) * nh;
    const cvOut = document.createElement("canvas"); cvOut.width = Math.max(1, Math.round(sw)); cvOut.height = Math.max(1, Math.round(sh));
    cvOut.getContext("2d").drawImage(im, sx, sy, sw, sh, 0, 0, cvOut.width, cvOut.height);
    // New logical canvas keeps the crop's aspect ratio so shapes scale uniformly (no distortion)
    const aspect = r.w / r.h;
    const ncw = BASE_W, nch = Math.max(1, Math.round(BASE_W / aspect));
    const k = ncw / r.w; // == nch / r.h (uniform)
    setBase(cvOut.toDataURL("image/png"));
    setCw(ncw); setCh(nch);
    setFtuX((v) => v / k);
    setFtuY((v) => v / k);
    setShapes((a) => a.map((s) => ({ ...s, points: s.points.map((q) => ({ x: (q.x - r.x) * k, y: (q.y - r.y) * k })) })));
    setGuideZone((g) => (g ? g.map((q) => ({ x: (q.x - r.x) * k, y: (q.y - r.y) * k })) : g));
    setBldZones((b) => (b ? b.map((poly) => poly.map((q) => ({ x: (q.x - r.x) * k, y: (q.y - r.y) * k }))) : b));
    setCropRect(null); setDraft(null); setTool("select");
    toast.success(zh ? "已按裁剪框裁剪，比例尺与图形已等比同步" : "Cropped to selection, scale & shapes kept proportional");
  };
  const onUp = () => {
    setSnapInd(null);
    setRotLive(null);
    if (dragRef.current) { const d = dragRef.current; dragRef.current = null; if (d.mode === "crophandle" || d.mode === "cropmove") return; if (d.moved && d.before) { histRef.current.past.push(d.before); if (histRef.current.past.length > 40) histRef.current.past.shift(); histRef.current.future = []; setHistTick((t) => t + 1); } return; }
    if (!draft) return;
    if (draft.type === "pen") { if (draft.points.length >= 2) addShape({ type: "pen", points: smoothPen(draft.points), penW: penWidth, penO: penOpacity / 100 }); setDraft(null); return; }
    if (draft.type === "line" || draft.type === "rect" || draft.type === "dim") { const [a, b] = draft.points; if (dist(a, b) > 4) addShape({ type: draft.type, points: [a, b] }); setDraft(null); return; }
    if (draft.type === "crop") { const r = rectNorm(draft.points); setDraft(null); if (r.w > 10 && r.h > 10) setCropRect(r); return; }
    if (draft.type === "scale") { const px = dist(draft.points[0], draft.points[1]); setDraft(null); if (px < 8) return;
      const v = window.prompt(zh ? "该线段的真实长度（英尺）？" : "Real length of this line (feet)?");
      const ft = Number(v); if (ft > 0) { pushHist(); setFtuX(ft / px); setFtuY(ft / px); setApprox(false); toast.success(zh ? "比例尺已标定" : "Scale calibrated"); }
      return; }
  };
  const onClick = (e) => {
    if (stage !== "draw") return;
    if (tool === "text") { const p = snapVertex(toPt(e)); setTextEdit({ x: p.x, y: p.y, value: "" }); }
    else if (tool === "polygon") {
      const p = snapVertex(toPt(e));
      const d = draft;
      if (!d || d.type !== "polygon") { setDraft({ type: "polygon", points: [p] }); return; }
      const first = d.points[0];
      if (d.points.length >= 2 && dist(p, first) < SNAP) {
        const pts = cleanPoly(d.points);
        if (pts.length >= 3) addShape({ type: "polygon", points: pts, closed: true });
        setDraft(null); setHoverPt(null);
        return;
      }
      setDraft({ ...d, points: [...d.points, p] });
    }
  };
  const commitTextEdit = () => {
    if (!textEdit) return;
    const t = String(textEdit.value || "").trim();
    if (t) addShape({ type: "text", points: [{ x: textEdit.x, y: textEdit.y }], text: t });
    setTextEdit(null);
  };
  const editLabelAt = (p) => {
    const near = [...shapes].reverse().find((s) => s.type !== "text" && s.type !== "icon" && s.type !== "pen" && shapeLabel(s, ftuX, ftuY, approx) && dist(labelPos(s), p) < 44);
    const cand = near || [...shapes].reverse().find((s) => hitShape(s, p) && s.type !== "text" && s.type !== "icon" && s.type !== "pen");
    if (!cand) return false;
    setSelId(cand.id);
    setLabelEdit({ id: cand.id, value: shapeLabel(cand, ftuX, ftuY, approx) });
    return true;
  };
  const commitLabelEdit = () => {
    if (!labelEdit) return;
    const { id, value } = labelEdit;
    pushHist();
    setShapes((a) => a.map((s) => {
      if (s.id !== id) return s;
      if (String(value).trim() === "") { const { customLabel, ...rest } = s; return rest; }
      return { ...s, customLabel: value };
    }));
    setLabelEdit(null);
  };
  const onDbl = (e) => {
    if (draft && draft.type === "polygon" && draft.points.length >= 3) { const pts = cleanPoly(draft.points); if (pts.length >= 3) addShape({ type: "polygon", points: pts, closed: true }); setDraft(null); setHoverPt(null); return; }
    if (tool === "select" && !draft) editLabelAt(toPt(e));
  };

  const rotateBy = (deg) => {
    if (!imgRef.current) return;
    pushHist();
    const im = imgRef.current;
    const cvR = document.createElement("canvas"); cvR.width = cw; cvR.height = ch;
    const ctx = cvR.getContext("2d");
    ctx.translate(cw / 2, ch / 2); ctx.rotate(deg * Math.PI / 180);
    ctx.drawImage(im, 0, 0, im.naturalWidth, im.naturalHeight, -cw / 2, -ch / 2, cw, ch);
    setBase(cvR.toDataURL("image/png"));
    setShapes((a) => a.map((s) => ({ ...s, points: s.points.map((q) => rotPt(q, deg, cw, ch)) })));
    setGuideZone((g) => (g ? g.map((q) => rotPt(q, deg, cw, ch)) : g));
    setBldZones((b) => (b ? b.map((poly) => poly.map((q) => rotPt(q, deg, cw, ch))) : b));
    if (Math.abs(ftuX - ftuY) / (ftuX || 1) > 0.02) setApprox(true);
  };
  const commitRotation = () => { if (!rotPreview) return; rotateBy(rotPreview); setRotPreview(0); };
  const closePoly = () => { if (draft && draft.type === "polygon" && draft.points.length >= 3) { const pts = cleanPoly(draft.points); if (pts.length >= 3) addShape({ type: "polygon", points: pts, closed: true }); setDraft(null); setHoverPt(null); } };
  const undoPolyPt = () => { setDraft((d) => (d && d.type === "polygon" ? (d.points.length > 1 ? { ...d, points: d.points.slice(0, -1) } : null) : d)); };

  // Del key delete
  useEffect(() => {
    if (!open || stage !== "draw") return;
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if ((e.key === "Delete" || e.key === "Backspace") && selId) { e.preventDefault(); delSel(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, stage, selId, shapes, base, ftuX, ftuY]); // eslint-disable-line

  const aduSf = Math.round(shapes.reduce((m, s) => Math.max(m, shapeAreaSf(s, ftuX, ftuY)), 0));

  const drawShapeCanvas = (ctx, s) => {
    ctx.strokeStyle = s.color; ctx.lineWidth = 2.5; ctx.lineJoin = "round";
    if (s.type === "line") { ctx.beginPath(); ctx.moveTo(s.points[0].x, s.points[0].y); ctx.lineTo(s.points[1].x, s.points[1].y); ctx.stroke(); }
    else if (s.type === "pen") {
      ctx.save(); ctx.globalAlpha = s.penO ?? 0.9; ctx.lineWidth = s.penW || 4; ctx.lineCap = "round";
      ctx.beginPath(); s.points.forEach((q, i) => i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)); ctx.stroke(); ctx.restore();
    }
    else if (s.type === "icon") {
      const p = s.points[0], sc = s.iconScale || 1, r = 11 * sc;
      ctx.save();
      if (s.icon === "tree" && s.canopyFt > 0 && ftuX > 0) {
        const cr = s.canopyFt / ftuX;
        ctx.fillStyle = "rgba(34,197,94,0.08)"; ctx.beginPath(); ctx.arc(p.x, p.y, cr, 0, 7); ctx.fill();
        ctx.strokeStyle = "#16a34a"; ctx.lineWidth = 1.5; ctx.setLineDash([6, 4]);
        ctx.beginPath(); ctx.arc(p.x, p.y, cr, 0, 7); ctx.stroke(); ctx.setLineDash([]);
        ctx.font = "600 9px sans-serif"; ctx.textAlign = "center";
        ctx.lineWidth = 2.2; ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.strokeText(`R ${s.canopyFt} ft`, p.x, p.y - cr - 3);
        ctx.fillStyle = "#166534"; ctx.fillText(`R ${s.canopyFt} ft`, p.x, p.y - cr - 3);
        ctx.textAlign = "left";
      }
      ctx.fillStyle = s.color; ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, 7); ctx.fill();
      ctx.strokeStyle = "#fff"; ctx.lineWidth = 2 * Math.min(sc, 1.6); ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, 7); ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.font = `bold ${Math.round(11 * sc)}px sans-serif`; ctx.textAlign = "center";
      ctx.fillText(s.iconGlyph || "?", p.x, p.y + 4 * sc);
      ctx.font = `bold ${Math.max(8, Math.round(9 * sc))}px sans-serif`; ctx.lineWidth = 2.5; ctx.strokeStyle = "rgba(255,255,255,0.95)";
      ctx.strokeText(s.iconLabel || "", p.x, p.y + r + Math.max(9, 11 * sc));
      ctx.fillStyle = "#111827"; ctx.fillText(s.iconLabel || "", p.x, p.y + r + Math.max(9, 11 * sc));
      ctx.textAlign = "left"; ctx.restore();
    }
    else if (s.type === "dim") { const [a, b] = s.points; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      const ang = Math.atan2(b.y - a.y, b.x - a.x) + Math.PI / 2, t = 6;
      [a, b].forEach((p) => { ctx.beginPath(); ctx.moveTo(p.x - Math.cos(ang) * t, p.y - Math.sin(ang) * t); ctx.lineTo(p.x + Math.cos(ang) * t, p.y + Math.sin(ang) * t); ctx.stroke(); }); }
    else if (s.type === "rect") { const r = rectNorm(s.points); ctx.fillStyle = rgba(s.color, s.fillOpacity); ctx.fillRect(r.x, r.y, r.w, r.h); ctx.strokeRect(r.x, r.y, r.w, r.h); }
    else if (s.type === "polygon") { ctx.beginPath(); s.points.forEach((q, i) => i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)); if (s.closed) ctx.closePath(); ctx.fillStyle = rgba(s.color, s.fillOpacity); if (s.closed) ctx.fill(); ctx.stroke(); }
    else if (s.type === "text") { ctx.font = "16px sans-serif"; ctx.fillStyle = s.color === "#ffffff" ? "#111827" : s.color; ctx.strokeStyle = "#fff"; ctx.lineWidth = 3; ctx.strokeText(s.text, s.points[0].x, s.points[0].y); ctx.fillText(s.text, s.points[0].x, s.points[0].y); }
    // 各边小字长度标注
    for (const el of edgeLabels(s, ftuX, ftuY)) {
      ctx.font = "600 9px sans-serif"; ctx.textAlign = "center";
      ctx.lineWidth = 2.4; ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.strokeText(el.text, el.x, el.y);
      ctx.fillStyle = s.labelColor || "#111827"; ctx.fillText(el.text, el.x, el.y);
      ctx.textAlign = "left";
    }
    const lbl = shapeLabel(s, ftuX, ftuY, approx);
    if (lbl && s.type !== "text") { const p = labelPos(s); const fs = s.labelSize || 13; ctx.font = `bold ${fs}px sans-serif`; ctx.textAlign = "center"; ctx.lineWidth = fs / 3.6; ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.strokeText(lbl, p.x, p.y); ctx.fillStyle = s.labelColor || "#111827"; ctx.fillText(lbl, p.x, p.y); ctx.textAlign = "left"; }
  };

  const addStamp = (ic) => {
    const jitter = () => (Math.random() - 0.5) * 40;
    addShape({ type: "icon", points: [{ x: cw / 2 + jitter(), y: ch / 2 + jitter() }], icon: ic.k,
               iconGlyph: zh ? ic.glyph : ic.gEn, iconLabel: zh ? ic.zh : ic.en, color: ic.color,
               ...(ic.k === "tree" ? { canopyFt: 10 } : {}) });
    setTool("select");
    toast.success(zh ? `${ic.zh}图标已添加，拖动摆放到位` : `${ic.en} icon added — drag to place`);
  };

  const saveMyIcons = (list) => { setMyIcons(list); api.put("/me/custom-icons", { icons: list }).catch(() => {}); };

  const placeCustomIcon = (label, iconColor) => {
    const jitter = () => (Math.random() - 0.5) * 40;
    addShape({ type: "icon", points: [{ x: cw / 2 + jitter(), y: ch / 2 + jitter() }], icon: "custom",
               iconGlyph: label.slice(0, 1).toUpperCase(), iconLabel: label, color: iconColor });
  };

  const addCustomStamp = () => {
    const t = customLabel.trim();
    if (!t) { toast.error(zh ? "请输入图标文字" : "Enter a label"); return; }
    placeCustomIcon(t, customColor);
    if (!myIcons.some((i) => i.label === t)) saveMyIcons([{ label: t, color: customColor }, ...myIcons].slice(0, 20));
    setCustomOpen(false); setCustomLabel("");
    toast.success(zh ? `自定义图标「${t}」已添加并存入常用库` : `Custom icon "${t}" added & saved`);
  };

  const savePlan = async (asNew = false) => {
    if (!shapes.length) { toast.error(zh ? "请先绘制至少一个图形" : "Draw something first"); return; }
    setBusy(true);
    try {
      // 高清导出：按截取底图原生分辨率(上限2.5x)渲染, 图形按逻辑坐标等比放大
      const natW = imgRef.current?.naturalWidth || cw;
      const S = Math.min(2.5, Math.max(1, natW / cw));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(cw * S); canvas.height = Math.round(ch * S);
      const ctx = canvas.getContext("2d");
      if (imgRef.current) ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
      ctx.save(); ctx.scale(S, S);
      shapes.forEach((s) => drawShapeCanvas(ctx, s));
      utilLinks.forEach((ul) => {
        const isPath = ul.kind === "path";
        ctx.strokeStyle = isPath ? "#16a34a" : (ul.color || "#0ea5e9"); ctx.lineWidth = isPath ? 2.5 : 1.5; ctx.setLineDash(isPath ? [9, 5] : [5, 4]);
        ctx.beginPath(); ctx.moveTo(ul.from.x, ul.from.y); ctx.lineTo(ul.to.x, ul.to.y); ctx.stroke(); ctx.setLineDash([]);
        const mx = (ul.from.x + ul.to.x) / 2, my = (ul.from.y + ul.to.y) / 2;
        const txt = isPath ? `${zh ? "步道" : "Path"} ≈ ${fmtFt(ul.distFt)}′` : `≈ ${fmtFt(ul.distFt)}′`;
        ctx.font = "600 10px sans-serif"; ctx.textAlign = "center";
        ctx.lineWidth = 2.4; ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.strokeText(txt, mx, my - 4);
        ctx.fillStyle = isPath ? "#14532d" : "#0c4a6e"; ctx.fillText(txt, mx, my - 4);
        ctx.textAlign = "left";
      });
      drawScaleBarCanvas(ctx, ftuX, ch);
      drawCompassCanvas(ctx, heading, cw);
      // 地界快照: APN/地块/可建参考区 作为角标烘焙进成品图(客户可读)
      if (parcel && (parcel.apn || parcel.lotSqft)) {
        const cap = [parcel.apn ? `APN ${parcel.apn}` : null,
          parcel.lotSqft ? `${zh ? "地块" : "Lot"} ${Number(parcel.lotSqft).toLocaleString()} sf` : null,
          setbackSqft ? `${zh ? "可建参考区" : "Buildable"} ~${setbackSqft.toLocaleString()} sf` : null].filter(Boolean).join(" · ");
        ctx.font = "bold 11px sans-serif";
        const tw = ctx.measureText(cap).width;
        const bx = cw - tw - 26, by = ch - 24;
        ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.fillRect(bx - 8, by - 14, tw + 16, 22);
        ctx.fillStyle = "#111827"; ctx.fillText(cap, bx, by + 1);
      }
      ctx.restore();
      const image = canvas.toDataURL("image/jpeg", 0.92);
      // clean base map (no shapes/compass) so re-editing doesn't double up baked shapes
      let baseImage = null;
      if (imgRef.current) {
        const bc = document.createElement("canvas"); bc.width = canvas.width; bc.height = canvas.height;
        bc.getContext("2d").drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
        baseImage = bc.toDataURL("image/jpeg", 0.92);
      }
      if (editingPlanId && !asNew) { try { await api.delete(`/crm/leads/${leadId}/layout-plans/${editingPlanId}`); } catch { /* ignore */ } }
      const baseName = name || `${zh ? "方案" : "Plan "}${plans.length + 1}`;
      const root = baseName.replace(/\s+v\d+$/, "");
      const vCount = plans.filter((p) => p.name === root || p.name.startsWith(root + " v")).length + 1;
      const finalName = asNew ? `${root} v${vCount}` : baseName;
      const { data } = await api.post(`/crm/leads/${leadId}/layout-plans`, {
        name: finalName, image, baseImage, shapes, center, zoom, cw, ch,
        ftPerUnit: ftuX, ftuX, ftuY, aduSf, perSf: Number(perSf) || null, is3d: approx, heading,
        bldZones: bldZones || null, guideZone: guideZone || null,
        siteCheck: {
          building: bldZones && bldZones.length
            ? (aduConflicts.some((c) => c.kind === "overlap") ? "overlap" : aduConflicts.some((c) => c.kind === "near") ? "near" : "clear")
            : "no_data",
          nearDistFt: (() => { const ns = aduConflicts.filter((c) => c.kind === "near"); return ns.length ? Number(Math.min(...ns.map((c) => c.distFt)).toFixed(1)) : null; })(),
          setback: guideZone && guideZone.length >= 3
            ? (aduConflicts.some((c) => c.kind === "setback") ? "out" : "in")
            : "no_data",
          treeFt: treeHints.length ? Number(Math.min(...treeHints.map((t) => t.distFt)).toFixed(1)) : null,
        },
        utilEst: utilLinks.length ? utilLinks.map((ul) => ({
          icon: ul.icon, kind: ul.kind, label: ul.label, distFt: Number(ul.distFt.toFixed(1)), low: ul.low, high: ul.high,
        })) : null,
      });
      toast.success(asNew ? (zh ? "已存为新版本" : "Saved as new version") : (zh ? "布局方案已保存，可继续编辑" : "Layout plan saved — keep editing"));
      loadPlans(); onSaved && onSaved(data);
      // stay on the current screenshot/drawing view; keep shapes so user can continue.
      // point editingPlanId at the freshly saved plan so a re-save updates it instead of duplicating.
      if (!asNew && data?.id) setEditingPlanId(data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "保存失败" : "Save failed")); }
    setBusy(false);
  };

  const renderPlan = async (pid) => {
    setRenderingId(pid);
    toast.info(zh ? "正在生成 AI 平面布局图，约需 10-30 秒…" : "Generating floor plan (10-30s)…");
    const opts = {};
    if (aiBeds !== "auto") opts.bedrooms = Number(aiBeds);
    if (aiBaths !== "auto") opts.bathrooms = Number(aiBaths);
    if (aiOrient !== "auto") opts.orientation = aiOrient;
    try { const { data } = await api.post(`/crm/leads/${leadId}/layout-plans/${pid}/render`, opts); toast.success(zh ? "AI 平面布局图已生成" : "Floor plan ready"); loadPlans(); return data; }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成失败，请重试" : "Failed")); }
    finally { setRenderingId(null); }
  };
  const toggleCmp = (id) => setCompareIds((a) => a.includes(id) ? a.filter((x) => x !== id) : (a.length >= 3 ? a : [...a, id]));

  const renderBatch = async () => {
    const targets = plans.filter((p) => !p.renderFileId);
    if (!targets.length) { toast.info(zh ? "所有方案都已生成 AI 平面图" : "All plans already have AI floor plans"); return; }
    const opts = {};
    if (aiBeds !== "auto") opts.bedrooms = Number(aiBeds);
    if (aiBaths !== "auto") opts.bathrooms = Number(aiBaths);
    if (aiOrient !== "auto") opts.orientation = aiOrient;
    setBatchBusy(true); setBatchProg({ done: 0, total: targets.length });
    toast.info(zh ? `正在批量生成 ${targets.length} 张 AI 平面图，请稍候…` : `Batch generating ${targets.length} floor plans…`);
    let done = 0;
    for (const p of targets) {
      setRenderingId(p.id);
      try { await api.post(`/crm/leads/${leadId}/layout-plans/${p.id}/render`, opts); } catch { /* keep going */ }
      done += 1; setBatchProg({ done, total: targets.length });
    }
    setRenderingId(null); setBatchBusy(false); setBatchProg(null);
    toast.success(zh ? `已批量生成 ${done}/${targets.length} 张 AI 平面图` : `Generated ${done}/${targets.length}`);
    loadPlans();
  };

  const editPlan = async (p) => {
    setBusy(true);
    try {
      const res = await api.get(`/files/${p.baseFileId || p.fileId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setBase(url); setCw(p.cw || 600); setCh(p.ch || 600); setFtuX(p.ftuX || p.ftPerUnit || 0); setFtuY(p.ftuY || p.ftPerUnit || 0); setApprox(!!p.is3d);
      setShapes((p.shapes || []).map((s) => ({ ...s, id: s.id || `s${Math.random().toString(36).slice(2, 8)}` })));
      setName(p.name || ""); setPerSf(p.perSf ? String(p.perSf) : ""); setOwnerNotes(p.ownerNotes || []);
      setHeading(p.heading || 0); setEditingPlanId(p.id); setSelId(null); setDraft(null); setTool("select");
      setBldZones(Array.isArray(p.bldZones) && p.bldZones.length ? p.bldZones : null);
      setGuideZone(Array.isArray(p.guideZone) && p.guideZone.length >= 3 ? p.guideZone : null);
      setRotPreview(0); setHoverPt(null); setCropRect(null);
      histRef.current = { past: [], future: [] }; setStage("draw");
      toast.info(zh ? "已载入方案，可在当前页继续编辑后另存" : "Loaded — edit & save here");
    } catch { toast.error(zh ? "载入失败" : "Failed to load"); }
    setBusy(false);
  };

  const delPlan = async (pid) => {
    try { await api.delete(`/crm/leads/${leadId}/layout-plans/${pid}`); loadPlans(); if (editingPlanId === pid) setEditingPlanId(null); toast.success(zh ? "已删除" : "Deleted"); }
    catch { toast.error(zh ? "删除失败" : "Failed"); }
  };

  if (!open) return null;
  const scaleTag = (ftuX && ftuY) ? (approx ? { c: "bg-amber-50 text-amber-700", t: zh ? "⚠ 近似比例（3D/旋转）" : "≈ approx scale" } : { c: "bg-emerald-50 text-emerald-700", t: zh ? "✓ 已按真实英尺" : "✓ real-foot scale" }) : { c: "bg-slate-100 text-slate-500", t: zh ? "未标定比例尺，请用「标定比例尺」" : "Not calibrated" };

  return (
    <div className={`fixed inset-0 z-[80] bg-black/70 flex items-center justify-center ${fullscreen ? "p-0" : "p-3"}`} data-testid="layout-draw-editor" onClick={onClose}>
      <div className={`bg-white flex flex-col overflow-hidden ${fullscreen ? "w-full h-full rounded-none" : "rounded-2xl w-full max-w-6xl max-h-[94vh]"}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <h3 className="font-bold text-slate-800 flex items-center gap-1.5"><MapIcon className="h-4 w-4 text-red-600" />{zh ? "卫星地图布局绘制 · ADU 方案" : "Satellite Layout Designer"}</h3>
          <div className="flex items-center gap-1">
            <button data-testid="layout-fullscreen-toggle" onClick={() => setFullscreen((v) => !v)} className="text-slate-400 hover:text-slate-700 p-1" title={fullscreen ? (zh ? "退出全屏" : "Exit fullscreen") : (zh ? "全屏" : "Fullscreen")}>{fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}</button>
            <button data-testid="layout-editor-close" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
          </div>
        </div>

        <div className={`flex-1 overflow-auto p-4 grid gap-4 ${fullscreen ? "md:grid-cols-[1fr_280px]" : "md:grid-cols-[1fr_260px]"}`}>
          <div>
            {stage === "frame" ? (
              <div className="space-y-2" data-testid="layout-frame-stage">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" data-testid="layout-2d3d-toggle" variant="outline" onClick={toggle3D} className="h-8 border-slate-300 text-slate-700">{is3D ? <><Box className="h-3.5 w-3.5 mr-1" />3D</> : <><MapIcon className="h-3.5 w-3.5 mr-1" />2D</>}</Button>
                  <Button size="sm" data-testid="layout-capture-screen" onClick={captureScreen} disabled={capturing || !mapObj} className="h-8 bg-brand-500 hover:bg-brand-600 text-white">{capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Camera className="h-3.5 w-3.5 mr-1" />{zh ? "屏幕截图（保留当前视角）" : "Screenshot"}</>}</Button>
                  <Button size="sm" data-testid="layout-fix-parcel" variant="outline" onClick={() => { setFixMode((v) => !v); setCand(null); }} disabled={!mapObj}
                    className={`h-8 ${fixMode ? "border-blue-400 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-600"}`}>
                    <Hexagon className="h-3.5 w-3.5 mr-1" />{fixMode ? (zh ? "点击地图选择地块…" : "Click a parcel…") : (zh ? "纠正地块" : "Fix parcel")}
                  </Button>
                  {parcel && (
                    <Button size="sm" data-testid="layout-parcel-toggle" variant="outline" onClick={() => setShowParcel((v) => !v)}
                      className={`h-8 ${showParcel ? "border-yellow-400 bg-yellow-50 text-yellow-700" : "border-slate-300 text-slate-500"}`}>
                      <Hexagon className="h-3.5 w-3.5 mr-1" />{showParcel ? (zh ? "地界线 开" : "Parcel ON") : (zh ? "地界线 关" : "Parcel OFF")}
                    </Button>
                  )}
                  {parcel && setbackCoords && (
                    <Button size="sm" data-testid="layout-setback-toggle" variant="outline" onClick={() => setShowSetback((v) => !v)}
                      className={`h-8 ${showSetback ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500"}`}>
                      <Square className="h-3.5 w-3.5 mr-1" />{showSetback ? (zh ? "可建参考区 开" : "Setback ON") : (zh ? "可建参考区 关" : "Setback OFF")}
                    </Button>
                  )}
                  {(buildings || []).length > 0 ? (
                    <Button size="sm" data-testid="layout-buildings-toggle" variant="outline" onClick={() => setShowBuildings((v) => !v)}
                      className={`h-8 ${showBuildings ? "border-orange-400 bg-orange-50 text-orange-700" : "border-slate-300 text-slate-500"}`}>
                      <Box className="h-3.5 w-3.5 mr-1" />{showBuildings ? (zh ? "建筑轮廓 开" : "Buildings ON") : (zh ? "建筑轮廓 关" : "Buildings OFF")}
                    </Button>
                  ) : bldTried && (
                    <Button size="sm" data-testid="layout-buildings-retry" variant="outline" onClick={refetchBuildings} disabled={bldBusy}
                      className="h-8 border-slate-300 text-slate-600">
                      {bldBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Box className="h-3.5 w-3.5 mr-1" />}{zh ? "识别建筑轮廓" : "Detect buildings"}
                    </Button>
                  )}
                  <Button size="sm" data-testid="layout-contours-toggle" variant="outline" onClick={toggleContours} disabled={contourBusy || !mapObj}
                    className={`h-8 ${showContours ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-300 text-slate-500"}`}>
                    {contourBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mountain className="h-3.5 w-3.5 mr-1" />}
                    {contourBusy ? (zh ? "生成等高线…" : "Contours…") : showContours ? (zh ? "等高线 开" : "Contours ON") : (zh ? "等高线" : "Contours")}
                  </Button>
                </div>
                {parcel && showParcel && (
                  <p className="text-[11px] text-yellow-700" data-testid="layout-parcel-info">
                    {zh ? "已按地址自动标注物业地界线（黄色）" : "Parcel boundary marked (yellow)"} · {parcel.source}{parcel.apn ? ` · APN ${parcel.apn}` : ""}{parcel.lotSqft ? ` · ${zh ? "地块约" : "lot ~"} ${Number(parcel.lotSqft).toLocaleString()} sf` : ""}
                    {setbackCoords && showSetback && (
                      <span data-testid="layout-setback-area" className="text-emerald-700 font-semibold">
                        {zh ? ` · 绿色虚线内可建参考区约 ${setbackSqft.toLocaleString()} sf` : ` · buildable guide ~${setbackSqft.toLocaleString()} sf`}
                        {parcel.aduMaxSf ? (zh ? `（州法独立 ADU 上限约 ${parcel.aduMaxSf} sf）` : ` (state ADU cap ~${parcel.aduMaxSf} sf)`) : ""}
                      </span>
                    )}
                    {setbackCoords && showSetback ? (zh ? " · 4ft 侧/后退界，前院及具体退界以当地规定为准" : " · 4ft side/rear setback guide") : ""}{zh ? "，截图时将一并带入" : ""}
                  </p>
                )}
                {!parcel && parcelMsg && <p className="text-[11px] text-slate-400" data-testid="layout-parcel-msg">{parcelMsg}</p>}
                {fixMode && (
                  <p className="text-[11px] text-blue-700 flex items-center gap-2 flex-wrap" data-testid="layout-cand-info">
                    {candBusy ? (zh ? "查询地块中…" : "Looking up parcel…")
                      : cand ? (
                        <>
                          <span className="font-semibold">{zh ? "候选地块（蓝色）" : "Candidate parcel (blue)"} · {cand.source}{cand.apn ? ` · APN ${cand.apn}` : ""}{cand.lotSqft ? ` · ${zh ? "地块约" : "lot ~"} ${Number(cand.lotSqft).toLocaleString()} sf` : ""}</span>
                          <button data-testid="layout-cand-use" onClick={useCand} className="text-[11px] rounded px-2 py-0.5 bg-blue-600 text-white hover:bg-blue-700">{zh ? "使用此地块" : "Use this parcel"}</button>
                          <button data-testid="layout-cand-cancel" onClick={() => setCand(null)} className="text-[11px] rounded px-2 py-0.5 border border-slate-200 bg-white text-slate-500">{zh ? "取消" : "Cancel"}</button>
                        </>
                      ) : (zh ? "地块识别不对？在地图上点击正确/相邻地块即可预览其红线，确认后替换" : "Wrong parcel? Click the correct/adjacent parcel to preview its boundary, then confirm to replace")}
                  </p>
                )}
                {(buildings || []).length > 0 && showBuildings && (
                  <p className="text-[11px] text-orange-700" data-testid="layout-buildings-info">
                    {zh ? `已标注现有建筑轮廓（主屋/车库等，共 ${buildings.length} 处，橙色），截图时将一并带入，便于摆放 ADU 时避让` : `${buildings.length} existing building outlines marked (orange) — baked into capture`}
                  </p>
                )}
                {showContours && contours && (
                  <p className="text-[11px] text-amber-700" data-testid="layout-contours-info">
                    {(contours.lines || []).length
                      ? (zh ? `等高线（棕色）：间距 ${contours.intervalFt} ft · 场地高程 ${contours.minFt}–${contours.maxFt} ft · 共 ${contours.lines.length} 条 · ${contours.source}，截图时将一并带入` : `Contours ${contours.intervalFt} ft interval · ${contours.minFt}–${contours.maxFt} ft · baked into capture`)
                      : (zh ? `地势平坦：场地高差仅 ${(contours.maxFt - contours.minFt).toFixed(1)} ft（${contours.minFt}–${contours.maxFt} ft），无需等高线` : `Flat site: only ${(contours.maxFt - contours.minFt).toFixed(1)} ft variation`)}
                  </p>
                )}
                {showContours && contours?.flow && (
                  <p className="text-[11px] text-sky-700" data-testid="layout-flow-info">
                    {zh
                      ? `坡向/排水方向（蓝色箭头）：向${cardinalOf(contours.flow.bearingDeg, true)}下降 · 平均坡度约 ${contours.flow.pct}% · 初步排水判断，工程排水以土建设计为准，截图时将一并带入`
                      : `Drainage flow (blue arrow): descending ${cardinalOf(contours.flow.bearingDeg, false)} · ~${contours.flow.pct}% avg grade — baked into capture`}
                  </p>
                )}
                {slope && (
                  <p className={`text-[11px] ${slope.level === "steep" ? "text-red-600 font-bold" : slope.level === "medium" ? "text-amber-600" : "text-slate-400"}`} data-testid="layout-slope-flag">
                    {slope.level === "steep"
                      ? (zh ? `⚠ 陡坡地块：地块内高差约 ${slope.dropFt} ft（坡度约 ${slope.pct}%）— 开挖/挡土墙/基础成本可能显著上浮${parcel?.slopeSurchargePct ? `，造价区间已自动上浮 ${parcel.slopeSurchargePct}%` : "，报价请预留余量"}` : `Steep slope: ~${slope.dropFt} ft drop (~${slope.pct}%)${parcel?.slopeSurchargePct ? ` — cost +${parcel.slopeSurchargePct}%` : ""}`)
                      : slope.level === "medium"
                        ? (zh ? `坡度中等：地块内高差约 ${slope.dropFt} ft（约 ${slope.pct}%）${parcel?.slopeSurchargePct ? `，造价区间已自动上浮 ${parcel.slopeSurchargePct}%` : "，基础/排水成本或有增加"}` : `Moderate slope: ~${slope.dropFt} ft (~${slope.pct}%)${parcel?.slopeSurchargePct ? ` — cost +${parcel.slopeSurchargePct}%` : ""}`)
                        : (zh ? `地形平缓（地块内高差约 ${slope.dropFt} ft）` : `Mostly flat (~${slope.dropFt} ft drop)`)}
                  </p>
                )}
                <p className="text-[11px] text-slate-400">{zh ? "拖动/缩放/旋转地图取景，可切 3D。「屏幕截图」将保留你当前看到的 Google 地图视角，请在授权框中选择「当前标签页」。" : "Pan/zoom/rotate to frame; screenshot keeps your exact Google view."}</p>
                <div ref={mapWrapRef} className="w-full mx-auto rounded-xl overflow-hidden border border-slate-200 bg-slate-100 relative" style={{ aspectRatio: `${BASE_W} / ${BASE_H}`, maxWidth: fullscreen ? `min(1400px, 92vw, calc(74vh * ${BASE_W} / ${BASE_H}))` : `min(900px, calc(72vh * ${BASE_W} / ${BASE_H}))` }}>
                  {loadError ? (
                    <GoogleMapError zh={zh} errorCode={errorCode} className="h-full" testId="layout-draw-map-error" />
                  ) : isLoaded && center ? (
                    <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} center={asLL(center)} zoom={zoom}
                      options={{ mapId, mapTypeId: "satellite", tilt: is3D ? 47.5 : 0, heading, disableDefaultUI: true, gestureHandling: "greedy", maxZoom: 22 }}
                      onLoad={(m) => { setMapObj(m); if (heading) m.setHeading(heading); }} onClick={onMapPick}>
                      {parcel && showParcel && (parcel.coords || []).length >= 3 && (
                        <Polygon paths={parcel.coords.map((c) => ({ lat: c[0], lng: c[1] }))}
                          options={{ strokeColor: "#facc15", strokeWeight: 3, strokeOpacity: 0.95, fillOpacity: 0, clickable: false, zIndex: 50 }} />
                      )}
                      {parcel && showParcel && showSetback && setbackCoords && (
                        <>
                          <Polygon paths={setbackCoords.map((c) => ({ lat: c[0], lng: c[1] }))}
                            options={{ strokeOpacity: 0, strokeWeight: 0, fillColor: "#22c55e", fillOpacity: 0.10, clickable: false, zIndex: 49 }} />
                          <Polyline path={[...setbackCoords, setbackCoords[0]].map((c) => ({ lat: c[0], lng: c[1] }))}
                            options={{ strokeOpacity: 0, clickable: false, zIndex: 51,
                              icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeWeight: 2, scale: 2, strokeColor: "#22c55e" }, offset: "0", repeat: "10px" }] }} />
                        </>
                      )}
                      {showBuildings && (buildings || []).map((poly, bi) => (
                        <Polygon key={`bld-${bi}`} paths={poly.map((c) => ({ lat: c[0], lng: c[1] }))}
                          options={{ strokeColor: "#fb923c", strokeWeight: 2.5, strokeOpacity: 0.95, fillColor: "#fb923c", fillOpacity: 0.12, clickable: false, zIndex: 52 }} />
                      ))}
                      {showContours && (contours?.lines || []).map((ln, ci) => (
                        <Polyline key={`ctr-${ci}`} path={ln.coords.map((c) => ({ lat: c[0], lng: c[1] }))}
                          options={{ strokeColor: "#f59e0b", strokeWeight: ln.elevFt % ((contours.intervalFt || 1) * 5) === 0 ? 2.6 : 1.5, strokeOpacity: 0.9, clickable: false, zIndex: 48 }} />
                      ))}
                      {showContours && (contours?.lines || []).filter((ln) => ln.coords.length >= 5).map((ln, ci) => {
                        const mid = ln.coords[Math.floor(ln.coords.length / 2)];
                        return (
                          <Marker key={`ctrl-${ci}`} position={{ lat: mid[0], lng: mid[1] }} clickable={false}
                            icon={{ path: "M 0 0", scale: 0 }}
                            label={{ text: `${ln.elevFt}′`, fontSize: "10px", fontWeight: "700", color: "#fde68a" }} />
                        );
                      })}
                      {flowArrow && (
                        <>
                          <Polyline path={[flowArrow.from, flowArrow.to]}
                            options={{ strokeColor: "#38bdf8", strokeWeight: 4.5, strokeOpacity: 0.95, clickable: false, zIndex: 56,
                              icons: [{ icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 4.5, strokeColor: "#38bdf8", fillColor: "#38bdf8", fillOpacity: 1, strokeWeight: 2 }, offset: "100%" }] }} />
                          <Marker position={flowArrow.mid} clickable={false} icon={{ path: "M 0 0", scale: 0 }}
                            label={{ text: zh ? `坡向→${cardinalOf(contours.flow.bearingDeg, true)}` : `Flow ${cardinalOf(contours.flow.bearingDeg, false)}`, fontSize: "11px", fontWeight: "700", color: "#bae6fd" }} />
                        </>
                      )}
                      {cand && (cand.coords || []).length >= 3 && (
                        <Polygon paths={cand.coords.map((c) => ({ lat: c[0], lng: c[1] }))}
                          options={{ strokeColor: "#3b82f6", strokeWeight: 3, strokeOpacity: 0.95, fillColor: "#3b82f6", fillOpacity: 0.10, clickable: false, zIndex: 55 }} />
                      )}
                    </GoogleMap>
                  ) : <div className="w-full h-full flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}
                </div>
              </div>
            ) : (
              <div className="space-y-2" data-testid="layout-draw-stage">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {TOOLS.map((t) => (
                    <button key={t.k} data-testid={`layout-tool-${t.k}`} onClick={() => { setTool(t.k); setDraft(null); setCropRect(null); setHoverPt(null); }}
                      className={`inline-flex items-center gap-1 text-xs rounded-lg px-2 py-1.5 border transition-colors ${tool === t.k ? "bg-brand-500 border-brand-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-brand-300"}`}>
                      <t.Icon className="h-3.5 w-3.5" />{zh ? t.zh : t.en}
                    </button>
                  ))}
                  {ftuX > 0 && (
                    <span className="inline-flex items-center gap-1 ml-1 pl-2 border-l border-slate-200">
                      <span className="text-[10px] text-emerald-600 font-semibold">{zh ? "ADU 模板" : "ADU presets"}</span>
                      {[[400, 20, 20], [800, 20, 40], [1200, 30, 40]].map(([sf, wf, hf]) => (
                        <button key={sf} data-testid={`layout-adu-preset-${sf}`} onClick={() => addAduPreset(sf, wf, hf)}
                          className="text-[11px] rounded-lg border border-emerald-300 text-emerald-700 px-2 py-1 hover:bg-emerald-50">{sf} sf</button>
                      ))}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 ml-1 pl-2 border-l border-slate-200">
                    <span className="text-[10px] text-sky-600 font-semibold">{zh ? "常用图标" : "Icons"}</span>
                    {STAMP_ICONS.map((ic) => (
                      <button key={ic.k} data-testid={`layout-stamp-${ic.k}`} onClick={() => addStamp(ic)} title={zh ? ic.zh : ic.en}
                        className="inline-flex items-center gap-0.5 text-[11px] rounded-lg border border-slate-200 px-1.5 py-1 hover:border-sky-400 hover:bg-sky-50">
                        <span className="inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold text-white" style={{ background: ic.color }}>{zh ? ic.glyph : ic.gEn}</span>
                        <span className="text-slate-600">{zh ? ic.zh : ic.en}</span>
                      </button>
                    ))}
                    <button data-testid="layout-stamp-custom-btn" onClick={() => setCustomOpen((v) => !v)}
                      className={`inline-flex items-center gap-0.5 text-[11px] rounded-lg border px-1.5 py-1 ${customOpen ? "border-sky-500 bg-sky-50 text-sky-700" : "border-dashed border-slate-300 text-slate-500 hover:border-sky-400 hover:text-sky-700"}`}>
                      ＋{zh ? "自定义" : "Custom"}
                    </button>
                    {myIcons.map((ic, i) => (
                      <span key={`${ic.label}-${i}`} className="inline-flex items-center rounded-lg border border-sky-100 bg-sky-50/50">
                        <button data-testid={`layout-my-icon-${i}`} onClick={() => { placeCustomIcon(ic.label, ic.color); toast.success(zh ? `「${ic.label}」已添加` : `"${ic.label}" added`); }} title={zh ? "常用库图标，点击添加" : "Saved icon — click to place"}
                          className="inline-flex items-center gap-0.5 text-[11px] px-1.5 py-1 hover:bg-sky-100/60 rounded-l-lg">
                          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold text-white" style={{ background: ic.color }}>{(ic.label || "?").slice(0, 1).toUpperCase()}</span>
                          <span className="text-slate-600">{ic.label}</span>
                        </button>
                        <button data-testid={`layout-my-icon-del-${i}`} onClick={() => saveMyIcons(myIcons.filter((_, j) => j !== i))} title={zh ? "从常用库移除" : "Remove"}
                          className="text-slate-300 hover:text-red-500 pr-1 text-[10px]">×</button>
                      </span>
                    ))}
                  </span>
                </div>
                {customOpen && (
                  <div className="flex items-center gap-2 flex-wrap rounded-lg bg-sky-50 border border-sky-100 px-2 py-1.5 text-[11px]" data-testid="layout-custom-icon-form">
                    <span className="text-sky-700 font-semibold">{zh ? "自定义图标" : "Custom icon"}</span>
                    <input data-testid="layout-custom-icon-label" value={customLabel} maxLength={10} autoFocus
                      onChange={(e) => setCustomLabel(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addCustomStamp(); if (e.key === "Escape") setCustomOpen(false); }}
                      placeholder={zh ? "图标文字，如：热水器" : "Label, e.g. Heater"}
                      className="h-6 w-36 rounded border border-slate-200 px-2 text-[11px]" />
                    {COLORS.filter((c) => c !== "#ffffff").map((c) => (
                      <button key={c} data-testid={`layout-custom-icon-color-${c}`} onClick={() => setCustomColor(c)}
                        className={`h-4 w-4 rounded-full ${customColor === c ? "ring-2 ring-offset-1 ring-slate-500" : "border border-slate-200"}`} style={{ background: c }} />
                    ))}
                    <button data-testid="layout-custom-icon-add" onClick={addCustomStamp} className="text-xs rounded px-2 py-0.5 bg-sky-600 text-white hover:bg-sky-700">{zh ? "添加" : "Add"}</button>
                    <button data-testid="layout-custom-icon-cancel" onClick={() => setCustomOpen(false)} className="text-xs rounded px-2 py-0.5 border border-slate-200 bg-white text-slate-500">{zh ? "取消" : "Cancel"}</button>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-slate-500">{zh ? "旋转底图" : "Rotate"}</span>
                  <input data-testid="layout-rotate-slider" type="range" min="-180" max="180" step="1" value={rotPreview}
                    onChange={(e) => setRotPreview(Number(e.target.value))} onMouseUp={commitRotation} onTouchEnd={commitRotation} className="w-40" />
                  <span className="w-10 text-[11px] text-slate-600 tabular-nums">{rotPreview}°</span>
                  {rotPreview !== 0 && <button data-testid="layout-rotate-apply" onClick={commitRotation} className="text-xs rounded-lg px-2 py-1 border bg-brand-500 border-brand-500 text-white">{zh ? "应用" : "Apply"}</button>}
                  <span className="w-px h-5 bg-slate-200 mx-1" />
                  <button data-testid="layout-undo" onClick={undo} disabled={!canUndo} className="inline-flex items-center gap-1 text-xs rounded-lg px-2 py-1 border bg-white border-slate-200 text-slate-600 hover:border-brand-300 disabled:opacity-40"><Undo2 className="h-3.5 w-3.5" />{zh ? "上一步" : "Undo"}</button>
                  <button data-testid="layout-redo" onClick={redo} disabled={!canRedo} className="inline-flex items-center gap-1 text-xs rounded-lg px-2 py-1 border bg-white border-slate-200 text-slate-600 hover:border-brand-300 disabled:opacity-40"><Redo2 className="h-3.5 w-3.5" />{zh ? "下一步" : "Redo"}</button>
                  <Button size="sm" variant="outline" data-testid="layout-back-frame" onClick={() => setStage("frame")} className="h-7 text-xs border-slate-300 ml-1">{zh ? "重新取景" : "Reframe"}</Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[11px]">
                  <span className="text-slate-500">{zh ? "颜色" : "Color"}</span>
                  {COLORS.map((c) => <button key={c} data-testid={`layout-color-${c}`} onClick={() => { setColor(c); updSel({ color: c }); }} className={`h-5 w-5 rounded-full border ${(selShape ? selShape.color : color) === c ? "ring-2 ring-offset-1 ring-slate-500" : "border-slate-200"}`} style={{ background: c }} />)}
                  <span className="text-slate-500 ml-2">{zh ? "填充透明度" : "Fill"}</span>
                  <input data-testid="layout-fill-opacity" type="range" min="0" max="100" value={Math.round((selShape ? selShape.fillOpacity : fillOpacity) * 100)}
                    onChange={(e) => { const v = Number(e.target.value) / 100; setFillOpacity(v); updSel({ fillOpacity: v }); }} className="w-24" />
                  <span className="w-8 text-slate-500">{Math.round((selShape ? selShape.fillOpacity : fillOpacity) * 100)}%</span>
                  {selShape && <Button size="sm" variant="outline" data-testid="layout-delete-shape" onClick={delSel} className="h-7 text-xs border-red-200 text-red-600 ml-1"><Trash2 className="h-3 w-3 mr-1" />{zh ? "删除选中 (Del)" : "Delete (Del)"}</Button>}
                </div>
                {/* 固定高度上下文工具条: 避免出现/消失导致画布跳动 (#7) */}
                <div className="h-[44px] flex items-center overflow-x-auto overflow-y-hidden" data-testid="layout-context-bar">
                  {draft && draft.type === "polygon" ? (
                    <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap rounded-lg bg-indigo-50 border border-indigo-100 px-2 py-1.5 w-max" data-testid="layout-polygon-toolbar">
                      <span className="text-[11px] text-indigo-700">{zh ? `多段线绘制中：已 ${draft.points.length} 点，逐点点击，最后闭合成填充图形` : `Polyline: ${draft.points.length} pts`}</span>
                      <button data-testid="layout-poly-undo-pt" onClick={undoPolyPt} className="text-xs rounded px-2 py-0.5 border border-indigo-200 bg-white text-indigo-700">{zh ? "撤销上一点" : "Undo point"}</button>
                      <button data-testid="layout-poly-close" onClick={closePoly} disabled={draft.points.length < 3} className="text-xs rounded px-2 py-0.5 border border-emerald-500 bg-emerald-600 text-white disabled:opacity-40">{zh ? "闭合并填充" : "Close & fill"}</button>
                      <button data-testid="layout-poly-cancel" onClick={() => setDraft(null)} className="text-xs rounded px-2 py-0.5 border border-slate-200 bg-white text-slate-500">{zh ? "取消" : "Cancel"}</button>
                    </div>
                  ) : tool === "crop" ? (
                    <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap rounded-lg bg-sky-50 border border-sky-100 px-2 py-1.5 w-max" data-testid="layout-crop-toolbar">
                      <span className="text-[11px] text-sky-700">{cropRect ? (zh ? "拖动裁剪框四周的方块自由调节，满意后点「确认裁剪」" : "Drag the handles to adjust, then confirm") : (zh ? "在图上拖拽出一个裁剪框（可任意形状）" : "Drag a crop box on the image")}</span>
                      {cropRect && <button data-testid="layout-crop-confirm" onClick={() => applyCrop(cropRect)} className="text-xs rounded px-2 py-0.5 border border-sky-500 bg-sky-600 text-white">{zh ? "确认裁剪" : "Apply crop"}</button>}
                      {cropRect && <button data-testid="layout-crop-reset" onClick={() => setCropRect(null)} className="text-xs rounded px-2 py-0.5 border border-slate-200 bg-white text-slate-500">{zh ? "重新框选" : "Redraw"}</button>}
                    </div>
                  ) : tool === "pen" ? (
                    <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap rounded-lg bg-rose-50 border border-rose-100 px-2 py-1.5 w-max text-[11px]" data-testid="layout-pen-toolbar">
                      <span className="text-rose-700 font-semibold">{zh ? "手写画笔" : "Pen"}</span>
                      <span className="text-slate-500">{zh ? "笔触" : "Width"}</span>
                      <input data-testid="layout-pen-width" type="range" min="2" max="14" value={penWidth} onChange={(e) => setPenWidth(Number(e.target.value))} className="w-20" />
                      <span className="w-5 text-slate-600 tabular-nums">{penWidth}</span>
                      <span className="text-slate-500">{zh ? "浓度" : "Opacity"}</span>
                      <input data-testid="layout-pen-opacity" type="range" min="10" max="100" value={penOpacity} onChange={(e) => setPenOpacity(Number(e.target.value))} className="w-20" />
                      <span className="w-8 text-slate-600 tabular-nums">{penOpacity}%</span>
                      <span className="text-slate-400">{zh ? "颜色用上方色盘 · 支持鼠标/触屏随手写画" : "Color above · mouse/touch"}</span>
                    </div>
                  ) : tool === "eraser" ? (
                    <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap rounded-lg bg-rose-50 border border-rose-100 px-2 py-1.5 w-max text-[11px]" data-testid="layout-eraser-toolbar">
                      <span className="text-rose-700 font-semibold">{zh ? "橡皮擦" : "Eraser"}</span>
                      <span className="text-slate-500">{zh ? "大小" : "Size"}</span>
                      <input data-testid="layout-eraser-size" type="range" min="6" max="30" value={eraserR} onChange={(e) => setEraserR(Number(e.target.value))} className="w-20" />
                      <span className="w-5 text-slate-600 tabular-nums">{eraserR}</span>
                      <span className="text-slate-400">{zh ? "按住拖动擦除手写笔迹（仅笔迹，中段擦除会自动断开）" : "Drag to erase pen strokes (splits mid-stroke)"}</span>
                    </div>
                  ) : selShape && selShape.type === "icon" ? (
                    <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap rounded-lg bg-sky-50 border border-sky-100 px-2 py-1.5 w-max text-[11px]" data-testid="layout-icon-toolbar">
                      <span className="inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold text-white" style={{ background: selShape.color }}>{selShape.iconGlyph}</span>
                      <input data-testid="layout-icon-label-input" value={selShape.iconLabel || ""} maxLength={12}
                        onChange={(e) => { const v = e.target.value; updSel(selShape.icon === "custom" ? { iconLabel: v, iconGlyph: (v.trim().slice(0, 1).toUpperCase() || "?") } : { iconLabel: v }); }}
                        className="h-6 w-24 rounded border border-sky-200 bg-white px-1.5 text-[11px] text-sky-700 font-semibold" placeholder={zh ? "图标文字" : "Label"} />
                      <span className="text-slate-500 ml-1">{zh ? "图标大小" : "Icon size"}</span>
                      <input data-testid="layout-icon-scale" type="range" min="60" max="300" step="10"
                        value={Math.round((selShape.iconScale || 1) * 100)}
                        onChange={(e) => setIconScale(Number(e.target.value) / 100)} className="w-24" />
                      <span className="w-9 text-slate-600 tabular-nums">{Math.round((selShape.iconScale || 1) * 100)}%</span>
                      <button data-testid="layout-icon-scale-reset" onClick={() => setIconScale(1)} className="text-xs rounded px-2 py-0.5 border border-slate-200 bg-white text-slate-500">{zh ? "复位" : "Reset"}</button>
                      {selShape.icon === "tree" && (
                        <>
                          <span className="text-emerald-700 font-semibold ml-1">{zh ? "冠幅半径" : "Canopy R"}</span>
                          <input data-testid="layout-tree-canopy" type="range" min="5" max="30" step="1"
                            value={selShape.canopyFt || 10}
                            onChange={(e) => setShapes((a) => a.map((sp) => sp.id === selId ? { ...sp, canopyFt: Number(e.target.value) } : sp))} className="w-24" />
                          <span className="w-10 text-emerald-700 tabular-nums" data-testid="layout-tree-canopy-val">{selShape.canopyFt || 10} ft</span>
                          {!(ftuX > 0) && <span className="text-slate-400">{zh ? "（标定比例尺后显示冠幅圈）" : "(calibrate scale to show circle)"}</span>}
                        </>
                      )}
                    </div>
                  ) : selShape && ["rect", "line", "dim", "polygon"].includes(selShape.type) ? (
                    <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5 w-max text-[11px]" data-testid="layout-label-toolbar">
                      <span className="text-slate-500">{zh ? "尺寸/面积文字" : "Label text"}</span>
                      <span className="text-[10px] text-slate-400">{zh ? "(可在图上双击标注直接编辑)" : "(double-click label on canvas)"}</span>
                      <Input data-testid="layout-label-input"
                        value={selShape.customLabel !== undefined && selShape.customLabel !== null ? selShape.customLabel : shapeLabel(selShape, ftuX, ftuY, approx)}
                        onChange={(e) => setSelLabel(e.target.value)}
                        placeholder={zh ? "可自定义文字" : "Custom label"} className="h-7 text-xs w-44" />
                      {selShape.customLabel !== undefined && selShape.customLabel !== null && (
                        <button data-testid="layout-label-reset" onClick={resetSelLabel} className="text-xs rounded px-2 py-0.5 border border-slate-200 bg-white text-slate-500 hover:text-red-600">{zh ? "恢复自动" : "Auto"}</button>
                      )}
                      <span className="text-slate-500 ml-1">{zh ? "字号" : "Size"}</span>
                      {LABEL_SIZES.map((sz, i) => (
                        <button key={sz} data-testid={`layout-label-size-${sz}`} onClick={() => updSel({ labelSize: sz })}
                          className={`text-[11px] rounded px-1.5 py-0.5 border ${(selShape.labelSize || 13) === sz ? "bg-slate-700 border-slate-700 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}>{["S", "M", "L", "XL"][i]}</button>
                      ))}
                      <span className="text-slate-500 ml-1">{zh ? "文字色" : "Text"}</span>
                      {LABEL_COLORS.map((c) => (
                        <button key={c} data-testid={`layout-label-color-${c}`} onClick={() => updSel({ labelColor: c })}
                          className={`h-4 w-4 rounded-full border shrink-0 ${(selShape.labelColor || "#111827") === c ? "ring-2 ring-offset-1 ring-slate-400" : "border-slate-200"}`} style={{ background: c }} />
                      ))}
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-300 px-1" data-testid="layout-context-hint">{zh ? "提示：选中图形可编辑标注/角落圆点旋转；尺寸标注会自动吸附图形顶点" : "Select a shape to edit label / rotate via corner dots"}</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[11px] flex-wrap" data-testid="layout-scale-readout">
                  <span className={`rounded-full px-2 py-0.5 ${scaleTag.c}`}>{scaleTag.t}</span>
                  {aduSf > 0 && <span className="text-slate-600" data-testid="layout-adu-area">{zh ? "最大 ADU 面积估算" : "Est. ADU area"}：<b className="text-red-600">{aduSf.toLocaleString()} sf</b></span>}
                </div>
                {aduConflicts.length > 0 && (
                  <div data-testid="layout-adu-conflict-banner" className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span data-testid="layout-adu-conflict-text">
                      {(() => {
                        const near = aduConflicts.filter((c) => c.kind === "near");
                        const parts = [
                          aduConflicts.some((c) => c.kind === "overlap") && (zh ? "ADU 与现有建筑轮廓重叠（红色高亮），请拖动避开" : "ADU overlaps an existing building — drag it clear"),
                          aduConflicts.some((c) => c.kind === "setback") && (zh ? "ADU 超出绿色可建参考区（4 ft 退界），请移回界内" : "ADU is outside the setback guide area"),
                          near.length > 0 && (zh
                            ? `ADU 距现有建筑约 ${Math.min(...near.map((c) => c.distFt)).toFixed(1)} ft，低于 ${MIN_BLD_CLEAR_FT} ft 参考间距`
                            : `ADU is ~${Math.min(...near.map((c) => c.distFt)).toFixed(1)} ft from a building (< ${MIN_BLD_CLEAR_FT} ft guide)`),
                        ].filter(Boolean);
                        return parts.join(" · ") + (zh ? "（不阻止保存，具体以当地规定为准）" : "");
                      })()}
                    </span>
                  </div>
                )}
                {treeHints.length > 0 && (
                  <div data-testid="layout-tree-hint-banner" className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span data-testid="layout-tree-hint-text">
                      {zh
                        ? `ADU 距树木约 ${Math.min(...treeHints.map((t) => t.distFt)).toFixed(1)} ft，位于冠幅范围内 — 注意树根/树冠的施工间距与修剪`
                        : `ADU is ~${Math.min(...treeHints.map((t) => t.distFt)).toFixed(1)} ft from a tree — inside canopy radius, mind roots/clearance`}
                    </span>
                  </div>
                )}
                {utilLinks.length > 0 && (
                  <div data-testid="layout-util-est" className="rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[11px]">
                    <span className="font-bold text-sky-800 mr-2">{zh ? "接管线/步道费用初步估算" : "Hookup / walkway rough estimate"}</span>
                    {utilLinks.map((ul) => (
                      <span key={`ue-${ul.id}`} className="inline-flex items-center gap-1 mr-3 text-slate-600" data-testid={`layout-util-est-${ul.id}`}>
                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: ul.kind === "path" ? "#16a34a" : ul.color }} />
                        {ul.label} ≈{fmtFt(ul.distFt)} ft · <b className="text-sky-800">${ul.low.toLocaleString()}–${ul.high.toLocaleString()}</b>
                      </span>
                    ))}
                    <span className="font-bold text-sky-900" data-testid="layout-util-est-total">
                      {zh ? "合计约" : "Total ~"} ${utilLinks.reduce((s, u) => s + u.low, 0).toLocaleString()}–${utilLinks.reduce((s, u) => s + u.high, 0).toLocaleString()}
                    </span>
                    <span className="block text-[10px] text-slate-400 mt-0.5">{zh ? "按开挖布管/铺装经验单价估算，最终以现场勘测与市政要求为准" : "Based on typical trenching/paving unit costs; final subject to site survey & utility requirements"}</span>
                  </div>
                )}
                <div className="relative mx-auto rounded-xl overflow-hidden border border-slate-300 select-none bg-slate-100 w-full" style={{ aspectRatio: `${cw} / ${ch}`, maxWidth: fullscreen ? `min(1400px, 92vw, calc(74vh * ${cw} / ${ch}))` : `min(900px, calc(72vh * ${cw} / ${ch}))` }}>
                  {base && <img ref={imgRef} src={base} alt="satellite" crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-fill pointer-events-none" style={{ transform: rotPreview ? `rotate(${rotPreview}deg)` : undefined }} />}
                  <svg ref={svgRef} data-testid="layout-svg" viewBox={`0 0 ${cw} ${ch}`} className="absolute inset-0 w-full h-full"
                    style={{ cursor: tool === "select" ? "move" : "crosshair", touchAction: "none" }}
                    onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} onClick={onClick} onDoubleClick={onDbl}>
                    <g transform={rotPreview ? `rotate(${rotPreview} ${cw / 2} ${ch / 2})` : undefined}>
                    {shapes.map((s) => <ShapeSVG key={s.id} s={s} selected={s.id === selId} fx={ftuX} fy={ftuY} approx={approx} />)}
                    {aduConflicts.map((cf) => (
                      <g key={`cf-${cf.id}-${cf.kind}`} pointerEvents="none" data-testid={`layout-adu-conflict-${cf.kind}`}>
                        {cf.bldIdx.map((bi) => (bldZones?.[bi] ? (
                          <polygon key={bi} points={bldZones[bi].map((p) => `${p.x},${p.y}`).join(" ")}
                            fill="rgba(239,68,68,0.15)" stroke="#ef4444" strokeWidth={2.5} />
                        ) : null))}
                        <polygon points={cf.corners.map((p) => `${p.x},${p.y}`).join(" ")}
                          fill="none" stroke="#ef4444" strokeWidth={3.5} strokeDasharray="9 5" />
                      </g>
                    ))}
                    {utilLinks.map((ul) => (
                      <g key={`ul-${ul.id}`} pointerEvents="none" data-testid={`layout-util-link-${ul.id}`}>
                        <line x1={ul.from.x} y1={ul.from.y} x2={ul.to.x} y2={ul.to.y}
                          stroke={ul.kind === "path" ? "#16a34a" : (ul.color || "#0ea5e9")}
                          strokeWidth={ul.kind === "path" ? 2.5 : 1.5} strokeDasharray={ul.kind === "path" ? "9 5" : "5 4"} opacity={0.9} />
                        <text x={(ul.from.x + ul.to.x) / 2} y={(ul.from.y + ul.to.y) / 2 - 4} textAnchor="middle" fontSize={10} fontWeight="600"
                          fill={ul.kind === "path" ? "#14532d" : "#0c4a6e"} stroke="#fff" strokeWidth={1.8} paintOrder="stroke">
                          {ul.kind === "path" ? `${zh ? "步道" : "Path"} ≈ ${fmtFt(ul.distFt)}′` : `≈ ${fmtFt(ul.distFt)}′`}
                        </text>
                      </g>
                    ))}
                    {draft && draft.type === "crop" && (() => { const r = rectNorm(draft.points); return <rect x={r.x} y={r.y} width={r.w} height={r.h} stroke="#0ea5e9" strokeWidth={2} strokeDasharray="6 4" fill="rgba(14,165,233,0.15)" />; })()}
                    {draft && draft.type === "scale" && <line x1={draft.points[0].x} y1={draft.points[0].y} x2={draft.points[1].x} y2={draft.points[1].y} stroke="#0ea5e9" strokeWidth={2.5} strokeDasharray="4 3" />}
                    {draft && (draft.type === "line" || draft.type === "rect" || draft.type === "polygon" || draft.type === "dim" || draft.type === "pen") && <ShapeSVG s={{ ...draft, points: draft.type === "pen" ? smoothPen(draft.points) : draft.points, color, fillOpacity, penW: penWidth, penO: penOpacity / 100 }} fx={ftuX} fy={ftuY} approx={approx} draftMode />}
                    {draft && draft.type === "polygon" && hoverPt && <line x1={draft.points[draft.points.length - 1].x} y1={draft.points[draft.points.length - 1].y} x2={hoverPt.x} y2={hoverPt.y} stroke={color} strokeWidth={2} strokeDasharray="5 4" />}
                    {draft && draft.type === "polygon" && draft.points.map((q, i) => <circle key={i} cx={q.x} cy={q.y} r={i === 0 ? 6 : 4} fill={i === 0 ? "#fff" : color} stroke={color} strokeWidth={2} />)}
                    {snapInd && (
                      <g pointerEvents="none" data-testid="layout-snap-indicator">
                        <circle cx={snapInd.x} cy={snapInd.y} r={8} fill="none" stroke="#22c55e" strokeWidth={2.5} />
                        <circle cx={snapInd.x} cy={snapInd.y} r={2.5} fill="#22c55e" />
                      </g>
                    )}
                    {tool === "eraser" && eraserPt && (
                      <circle data-testid="layout-eraser-cursor" cx={eraserPt.x} cy={eraserPt.y} r={eraserR} fill="rgba(244,63,94,0.12)" stroke="#f43f5e" strokeWidth={1.5} strokeDasharray="4 3" pointerEvents="none" />
                    )}
                    {rotLive && (
                      <g pointerEvents="none" data-testid="layout-rot-angle">
                        <rect x={rotLive.x - 27} y={rotLive.y - 46} width={54} height={20} rx={5} fill="rgba(15,23,42,0.85)" />
                        <text x={rotLive.x} y={rotLive.y - 32} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="700">{rotLive.deg}°</text>
                      </g>
                    )}
                    {selShape && handlesOf(selShape).map((h) => <rect key={h.k} data-testid={`layout-handle-${h.k}`} x={h.pt.x - 5} y={h.pt.y - 5} width={10} height={10} fill="#fff" stroke="#ef4444" strokeWidth={2} />)}
                    {selShape && rotHandlesOf(selShape).map((h) => (
                      <g key={h.k} data-testid={`layout-rot-handle-${h.k}`} style={{ cursor: "grab" }}>
                        <circle cx={h.pt.x} cy={h.pt.y} r={7} fill="#fff" stroke="#6366f1" strokeWidth={2} />
                        <path d={`M ${h.pt.x - 3.2} ${h.pt.y} a 3.2 3.2 0 1 1 1.4 2.6`} fill="none" stroke="#6366f1" strokeWidth={1.5} />
                        <path d={`M ${h.pt.x - 2} ${h.pt.y + 3.6} l -1.6 -1.4 l 2.2 -0.6 z`} fill="#6366f1" />
                      </g>
                    ))}
                    </g>
                    {cropRect && (
                      <g data-testid="layout-crop-box">
                        <rect x={cropRect.x} y={cropRect.y} width={cropRect.w} height={cropRect.h} stroke="#0ea5e9" strokeWidth={2} fill="rgba(14,165,233,0.12)" />
                        {cropHandles(cropRect).map((h) => <rect key={h.k} data-testid={`layout-crop-handle-${h.k}`} x={h.pt.x - 6} y={h.pt.y - 6} width={12} height={12} fill="#fff" stroke="#0ea5e9" strokeWidth={2} style={{ cursor: "pointer" }} />)}
                      </g>
                    )}
                    {ownerNotes.map((n, i) => (
                      <g key={n.id || i} data-testid={`layout-owner-note-${i}`}>
                        <circle cx={n.x * cw} cy={n.y * ch} r={11} fill="#f59e0b" stroke="#fff" strokeWidth={2} />
                        <text x={n.x * cw} y={n.y * ch + 4} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">{i + 1}</text>
                        <title>{n.text}</title>
                      </g>
                    ))}
                    {scaleBar(ftuX) && (() => { const sb = scaleBar(ftuX); return (
                      <g pointerEvents="none">
                        <rect x={12} y={ch - 42} width={sb.px + 14} height={28} fill="rgba(255,255,255,0.82)" />
                        <line x1={18} y1={ch - 24} x2={18 + sb.px} y2={ch - 24} stroke="#111827" strokeWidth={2} />
                        <line x1={18} y1={ch - 29} x2={18} y2={ch - 19} stroke="#111827" strokeWidth={2} />
                        <line x1={18 + sb.px} y1={ch - 29} x2={18 + sb.px} y2={ch - 19} stroke="#111827" strokeWidth={2} />
                        <text x={18} y={ch - 30} fill="#111827" fontSize="11" fontWeight="700">{sb.ft} ft</text>
                      </g>
                    ); })()}
                    {(() => { const a = northAngle(heading); const cx = cw - 46, cy = 46, r = 18; return (
                      <g pointerEvents="none" data-testid="layout-compass">
                        <circle cx={cx} cy={cy} r={r + 5} fill="rgba(255,255,255,0.82)" />
                        <line x1={cx - Math.cos(a) * r} y1={cy - Math.sin(a) * r} x2={cx + Math.cos(a) * r} y2={cy + Math.sin(a) * r} stroke="#334155" strokeWidth={2} />
                        <polygon points={`${cx + Math.cos(a) * r},${cy + Math.sin(a) * r} ${cx + Math.cos(a + 2.7) * r * 0.55},${cy + Math.sin(a + 2.7) * r * 0.55} ${cx + Math.cos(a - 2.7) * r * 0.55},${cy + Math.sin(a - 2.7) * r * 0.55}`} fill="#ef4444" />
                        <text x={cx + Math.cos(a) * (r + 9)} y={cy + Math.sin(a) * (r + 9) + 4} textAnchor="middle" fill="#111827" fontSize="12" fontWeight="700">N</text>
                      </g>
                    ); })()}
                  </svg>
                  {textEdit && (
                    <input
                      autoFocus
                      data-testid="layout-text-inline-input"
                      value={textEdit.value}
                      onChange={(e) => setTextEdit((s) => ({ ...s, value: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitTextEdit(); } else if (e.key === "Escape") { e.preventDefault(); setTextEdit(null); } }}
                      onBlur={commitTextEdit}
                      placeholder={zh ? "直接输入文字，回车确认" : "Type, Enter to save"}
                      className="absolute z-30 -translate-y-1/2 px-2 py-1 rounded-md border border-red-400 bg-white/95 text-[13px] text-slate-800 shadow-lg outline-none min-w-[150px]"
                      style={{ left: `${(textEdit.x / cw) * 100}%`, top: `${(textEdit.y / ch) * 100}%` }}
                    />
                  )}
                  {labelEdit && (() => {
                    const sh = shapes.find((s) => s.id === labelEdit.id);
                    if (!sh) return null;
                    const lp = labelPos(sh);
                    return (
                      <input
                        autoFocus
                        data-testid="layout-label-inline-input"
                        value={labelEdit.value}
                        onChange={(e) => setLabelEdit((s) => ({ ...s, value: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitLabelEdit(); } else if (e.key === "Escape") { e.preventDefault(); setLabelEdit(null); } }}
                        onBlur={commitLabelEdit}
                        placeholder={zh ? "输入标注，回车确认，留空恢复自动" : "Label, Enter to save"}
                        className="absolute z-30 -translate-x-1/2 -translate-y-1/2 px-2 py-1 rounded-md border border-indigo-400 bg-white text-[12px] text-slate-800 shadow-lg outline-none min-w-[140px]"
                        style={{ left: `${(lp.x / cw) * 100}%`, top: `${(lp.y / ch) * 100}%` }}
                      />
                    );
                  })()}
                </div>
                {ownerNotes.length > 0 && (
                  <div className="max-w-[760px] mx-auto rounded-lg border border-amber-200 bg-amber-50 p-2" data-testid="layout-owner-notes-list">
                    <p className="text-[11px] font-semibold text-amber-700 mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" />{zh ? `业主批注 (${ownerNotes.length})` : `Homeowner notes (${ownerNotes.length})`}</p>
                    <ol className="list-decimal list-inside space-y-0.5">{ownerNotes.map((n, i) => <li key={n.id || i} className="text-[11px] text-slate-600">{n.text}</li>)}</ol>
                  </div>
                )}
                <div className="flex items-center gap-2 max-w-[760px] mx-auto">
                  <Input data-testid="layout-plan-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={zh ? "方案名称，如 方案一 / 后院方案" : "Plan name"} className="h-8 text-xs flex-1" />
                  <div className="flex items-center gap-1 shrink-0"><span className="text-[11px] text-slate-500">$</span>
                    <Input data-testid="layout-plan-persf" type="number" value={perSf} onChange={(e) => setPerSf(e.target.value)} placeholder={zh ? "建议单价/sf" : "$/sf"} className="h-8 text-xs w-24" /></div>
                  <Button size="sm" data-testid="layout-save-plan" onClick={() => savePlan(false)} disabled={busy || !shapes.length} className="h-8 bg-brand-500 hover:bg-brand-600 text-white shrink-0">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-1" />{editingPlanId ? (zh ? "更新方案" : "Update") : (zh ? "保存为图片方案" : "Save plan")}</>}</Button>
                  {editingPlanId && <Button size="sm" variant="outline" data-testid="layout-save-version" onClick={() => savePlan(true)} disabled={busy || !shapes.length} className="h-8 border-slate-300 text-slate-700 shrink-0 text-xs">{zh ? "存为新版本" : "Save as version"}</Button>}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs font-semibold text-slate-500 flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{zh ? `已保存方案 (${plans.length})` : `Saved plans (${plans.length})`}</p>
              <div className="flex items-center gap-2">
                {compareIds.length >= 2 && <button data-testid="layout-compare-btn" onClick={() => setShowCompare(true)} className="text-[11px] text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"><Images className="h-3.5 w-3.5" />{zh ? `并排对比 (${compareIds.length})` : `Compare (${compareIds.length})`}</button>}
                {plans.some((p) => !p.renderFileId) && <button data-testid="layout-batch-render" onClick={renderBatch} disabled={batchBusy} className="text-[11px] text-fuchsia-600 hover:text-fuchsia-800 inline-flex items-center gap-1 disabled:opacity-50">{batchBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />{batchProg ? `${batchProg.done}/${batchProg.total}` : ""}</> : <><Sparkles className="h-3.5 w-3.5" />{zh ? "批量AI平面图" : "Batch AI"}</>}</button>}
                {plans.length > 0 && <ProposalShareButton api={api} leadId={leadId} zh={zh} className="text-[11px] text-violet-600 hover:text-violet-800 inline-flex items-center gap-1" />}
              </div>
            </div>
            {plans.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-fuchsia-200 bg-fuchsia-50 px-2.5 py-1.5" data-testid="ai-plan-prefs">
                <span className="text-[11px] font-medium text-fuchsia-700 inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />{zh ? "AI 平面图偏好" : "AI plan prefs"}</span>
                <label className="text-[11px] text-slate-500 inline-flex items-center gap-1">{zh ? "卧室" : "Beds"}
                  <select data-testid="ai-pref-beds" value={aiBeds} onChange={(e) => setAiBeds(e.target.value)} className="h-6 rounded border border-slate-300 bg-white text-[11px] px-1">
                    <option value="auto">{zh ? "自动" : "Auto"}</option>{[0,1,2,3].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-slate-500 inline-flex items-center gap-1">{zh ? "卫生间" : "Baths"}
                  <select data-testid="ai-pref-baths" value={aiBaths} onChange={(e) => setAiBaths(e.target.value)} className="h-6 rounded border border-slate-300 bg-white text-[11px] px-1">
                    <option value="auto">{zh ? "自动" : "Auto"}</option>{[1,2,3].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="text-[11px] text-slate-500 inline-flex items-center gap-1">{zh ? "主卧朝向" : "Primary faces"}
                  <select data-testid="ai-pref-orient" value={aiOrient} onChange={(e) => setAiOrient(e.target.value)} className="h-6 rounded border border-slate-300 bg-white text-[11px] px-1">
                    <option value="auto">{zh ? "自动" : "Auto"}</option>
                    <option value="south">{zh ? "朝南" : "South"}</option>
                    <option value="north">{zh ? "朝北" : "North"}</option>
                    <option value="east">{zh ? "朝东" : "East"}</option>
                    <option value="west">{zh ? "朝西" : "West"}</option>
                  </select>
                </label>
              </div>
            )}
            <div className="space-y-2" data-testid="layout-plans-list">
              {plans.map((p) => (
                <div key={p.id} className={`rounded-lg border overflow-hidden ${editingPlanId === p.id ? "border-red-400 ring-1 ring-red-200" : compareIds.includes(p.id) ? "border-indigo-400 ring-1 ring-indigo-200" : "border-slate-200"}`} data-testid={`layout-plan-${p.id}`}>
                  <div className="relative">
                    <button onClick={() => editPlan(p)} className="block w-full" data-testid={`layout-plan-open-${p.id}`}>
                      <img src={authThumb(p.renderFileId || p.fileId)} alt={p.name} className="w-full h-28 object-cover" />
                    </button>
                    {p.renderFileId && <span className="absolute top-1 left-1 text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" data-testid={`layout-plan-render-badge-${p.id}`}><Sparkles className="h-2.5 w-2.5" />{zh ? "平面图" : "Plan"}</span>}
                    <button data-testid={`layout-plan-zoom-${p.id}`} onClick={(e) => { e.stopPropagation(); setZoomPlan(p); }} className="absolute bottom-1 right-1 bg-white/85 rounded p-1 text-slate-700 hover:text-brand-500" title={zh ? "放大查看" : "Zoom"}><ZoomIn className="h-3.5 w-3.5" /></button>
                    <label className="absolute top-1 right-1 bg-white/85 rounded px-1 py-0.5 flex items-center gap-1 cursor-pointer" title={zh ? "加入对比" : "Compare"}>
                      <input type="checkbox" data-testid={`layout-plan-cmp-${p.id}`} checked={compareIds.includes(p.id)} onChange={() => toggleCmp(p.id)} className="h-3 w-3" />
                    </label>
                  </div>
                  <div className="flex items-center justify-between px-2 py-1.5 gap-1">
                    <span className="text-xs font-medium text-slate-700 truncate flex-1">{p.name}{p.aduSf ? ` · ${Number(p.aduSf).toLocaleString()} sf` : ""}{p.perSf ? ` · $${Number(p.perSf).toLocaleString()}/sf` : ""}</span>
                    {(p.ownerNotes || []).length > 0 && <span className="text-[10px] text-amber-600 inline-flex items-center gap-0.5" data-testid={`layout-plan-notecount-${p.id}`}><MessageSquare className="h-3 w-3" />{p.ownerNotes.length}</span>}
                    <button data-testid={`layout-plan-render-${p.id}`} onClick={() => renderPlan(p.id)} disabled={renderingId === p.id} className="text-fuchsia-600 hover:text-fuchsia-800 disabled:opacity-50" title={zh ? "一键 AI 平面布局图" : "AI floor plan"}>{renderingId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}</button>
                    <a data-testid={`layout-plan-pdf-${p.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/leads/${leadId}/layout-plans/${p.id}/pdf?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-800" title={zh ? "方案书 PDF" : "Plan PDF"}><FileText className="h-3.5 w-3.5" /></a>
                    <button data-testid={`layout-plan-edit-${p.id}`} onClick={() => editPlan(p)} className="text-indigo-500 hover:text-indigo-700"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`layout-plan-del-${p.id}`} onClick={() => delPlan(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
              {plans.length === 0 && <p className="text-[11px] text-slate-300">{zh ? "暂无方案，绘制后保存即可供客户挑选、报价引用" : "No plans yet"}</p>}
            </div>
          </div>
        </div>
      </div>
      {showCompare && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-0" data-testid="layout-compare-modal" onClick={(e) => { e.stopPropagation(); setShowCompare(false); }}>
          <div className="bg-white w-full h-full rounded-none p-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 shrink-0">
              <h4 className="font-bold text-slate-800 text-sm flex items-center gap-1.5"><Images className="h-4 w-4 text-indigo-600" />{zh ? "方案版本并排对比" : "Compare plan versions"}</h4>
              <button data-testid="layout-compare-close" onClick={() => setShowCompare(false)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="flex-1 min-h-0 grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(compareIds.length, 3)}, minmax(0,1fr))` }}>
              {compareIds.map((id) => { const p = plans.find((x) => x.id === id); if (!p) return null; return (
                <div key={id} className="rounded-xl border border-slate-200 overflow-hidden flex flex-col min-h-0" data-testid={`layout-compare-item-${id}`}>
                  <div className="flex-1 min-h-0 bg-slate-100 flex items-center justify-center p-2">
                    <img src={authThumb(p.renderFileId || p.fileId)} alt={p.name} className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="p-2 text-xs shrink-0 border-t border-slate-100">
                    <p className="font-semibold text-slate-800 truncate">{p.name}</p>
                    {(() => { const d = planDims(p); return d ? <p className="text-slate-600" data-testid={`layout-compare-dims-${p.id}`}>{fmtFt(d.w)} × {fmtFt(d.l)} ft · {d.sf.toLocaleString()} sf</p> : null; })()}
                    <p className="text-slate-500">{p.aduSf ? `${Number(p.aduSf).toLocaleString()} sf` : ""}{p.perSf ? ` · $${Number(p.perSf).toLocaleString()}/sf` : ""}{p.aduSf && p.perSf ? ` · ≈$${Math.round(p.aduSf * p.perSf).toLocaleString()}` : ""}</p>
                  </div>
                </div>
              ); })}
            </div>
          </div>
        </div>
      )}
      {zoomPlan && (
        <div className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4" data-testid="layout-zoom-modal" onClick={(e) => { e.stopPropagation(); setZoomPlan(null); }}>
          <button data-testid="layout-zoom-close" onClick={() => setZoomPlan(null)} className="mobile-safe-close absolute top-4 right-4 text-white/80 hover:text-white"><X className="h-6 w-6" /></button>
          <div className="max-w-[92vw] max-h-[90vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img data-testid="layout-zoom-image" src={authThumb(zoomPlan.renderFileId || zoomPlan.fileId)} alt={zoomPlan.name} className="max-w-[92vw] max-h-[80vh] object-contain rounded-lg shadow-2xl" />
            <p className="text-white text-sm font-medium">{zoomPlan.name}{zoomPlan.aduSf ? ` · ${Number(zoomPlan.aduSf).toLocaleString()} sf` : ""}{zoomPlan.perSf ? ` · $${Number(zoomPlan.perSf).toLocaleString()}/sf` : ""}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const authThumb = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

function ShapeSVG({ s, selected, draftMode, fx, fy, approx }) {
  const stroke = s.color; const sw = selected ? 4 : 2.5;
  const dash = draftMode ? "6 4" : undefined;
  const lbl = !draftMode ? shapeLabel(s, fx, fy, approx) : "";
  const lp = lbl ? labelPos(s) : null;
  const Label = lp ? <text x={lp.x} y={lp.y} textAnchor="middle" fill={s.labelColor || "#111827"} stroke="#fff" strokeWidth={(s.labelSize || 13) / 4.3} paintOrder="stroke" fontSize={s.labelSize || 13} fontWeight="700">{lbl}</text> : null;
  const Edges = edgeLabels(s, fx, fy).map((el, i) => (
    <text key={`e${i}`} x={el.x} y={el.y} textAnchor="middle" fontSize={9} fontWeight="600" fill={s.labelColor || "#111827"} stroke="#fff" strokeWidth={1.6} paintOrder="stroke">{el.text}</text>
  ));
  if (s.type === "line") return <g><line x1={s.points[0].x} y1={s.points[0].y} x2={s.points[1].x} y2={s.points[1].y} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} />{Label}</g>;
  if (s.type === "pen") { const pts = s.points.map((q) => `${q.x},${q.y}`).join(" ");
    return <polyline points={pts} stroke={stroke} strokeWidth={(s.penW || 4) + (selected ? 2 : 0)} strokeOpacity={s.penO ?? 0.9} fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={dash} />; }
  if (s.type === "icon") { const p = s.points[0], sc = s.iconScale || 1, r = 11 * sc;
    const canopyR = s.icon === "tree" && s.canopyFt > 0 && fx > 0 ? s.canopyFt / fx : 0;
    return <g>
      {canopyR > 0 && (
        <g data-testid="layout-tree-canopy-circle">
          <circle cx={p.x} cy={p.y} r={canopyR} fill="rgba(34,197,94,0.08)" stroke="#16a34a" strokeWidth={1.5} strokeDasharray="6 4" />
          <text x={p.x} y={p.y - canopyR - 3} textAnchor="middle" fontSize={9} fontWeight="600" fill="#166534" stroke="#fff" strokeWidth={1.6} paintOrder="stroke">{`R ${s.canopyFt} ft`}</text>
        </g>
      )}
      <circle cx={p.x} cy={p.y} r={r} fill={s.color} stroke="#fff" strokeWidth={2 * Math.min(sc, 1.6)} opacity={0.95} />
      <text x={p.x} y={p.y + 4 * sc} textAnchor="middle" fill="#fff" fontSize={11 * sc} fontWeight="700">{s.iconGlyph}</text>
      <text x={p.x} y={p.y + r + Math.max(9, 11 * sc)} textAnchor="middle" fontSize={Math.max(8, 9 * sc)} fontWeight="600" fill="#111827" stroke="#fff" strokeWidth={1.8} paintOrder="stroke">{s.iconLabel}</text>
      {selected && <circle cx={p.x} cy={p.y} r={r + 4} fill="none" stroke="#ef4444" strokeWidth={2} strokeDasharray="4 3" />}
    </g>; }
  if (s.type === "dim") { const [a, b] = s.points; const ang = Math.atan2(b.y - a.y, b.x - a.x) + Math.PI / 2, t = 6; const tick = (p) => `${p.x - Math.cos(ang) * t},${p.y - Math.sin(ang) * t} ${p.x + Math.cos(ang) * t},${p.y + Math.sin(ang) * t}`;
    return <g><line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} /><polyline points={tick(a)} stroke={stroke} strokeWidth={sw} /><polyline points={tick(b)} stroke={stroke} strokeWidth={sw} />{Label}</g>; }
  if (s.type === "rect") { const r = rectNorm(s.points); return <g><rect x={r.x} y={r.y} width={r.w} height={r.h} stroke={stroke} strokeWidth={sw} fill={rgba(s.color, s.fillOpacity)} strokeDasharray={dash} />{Edges}{Label}</g>; }
  if (s.type === "polygon") { const pts = s.points.map((q) => `${q.x},${q.y}`).join(" "); return <g>{s.closed ? <polygon points={pts} stroke={stroke} strokeWidth={sw} fill={rgba(s.color, s.fillOpacity)} strokeDasharray={dash} /> : <polyline points={pts} stroke={stroke} strokeWidth={sw} fill="none" strokeDasharray={dash} />}{Edges}{Label}</g>; }
  if (s.type === "text") return <text x={s.points[0].x} y={s.points[0].y} fill={s.color === "#ffffff" ? "#111827" : s.color} stroke="#fff" strokeWidth="0.6" fontSize="16" fontWeight="600" paintOrder="stroke">{s.text}</text>;
  return null;
}
